import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import TableChartIcon from '@mui/icons-material/TableChart'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  orderService,
  adminOrderService,
  locationService,
} from '../services'
import SapSalesOrderStatus from './SapSalesOrderStatus'
import ShipmentCreateFailureStatus from './ShipmentCreateFailureStatus'
import AspenReceiveItemsStatus from './AspenReceiveItemsStatus'
import PurchaseOrderUpdateStatus from './PurchaseOrderUpdateStatus'
import ManualWebhookDialog from './ManualWebhookDialog'
import { getApiErrorMessage } from '../services/apiClient'
import { useLazyOrderSection } from './useLazyOrderSection'
import { downloadBlobResponse } from '../components/price-concerns/priceConcernUtils'
import CheckBalanceButton from '../components/CheckBalanceButton'
import OrderActionsPanel from '../components/OrderActionsPanel'
import CollapsibleSection from '../components/CollapsibleSection'
import PaymentInformationSection from '../components/orders/PaymentInformationSection'
import PaymentProofEntriesSection from '../components/orders/PaymentProofEntriesSection'
import AdminPaymentProofUploadDialog from '../components/orders/AdminPaymentProofUploadDialog'
import PaymentProofBalanceChoiceDialog from '../components/orders/PaymentProofBalanceChoiceDialog'
import { useAdminPaymentProofUploadGate } from '../hooks/useAdminPaymentProofUploadGate'
import PaymentProofVerifyDialog from '../components/orders/PaymentProofVerifyDialog'
import PaymentProofEditAfterRejectDialog from '../components/orders/PaymentProofEditAfterRejectDialog'
import ConfirmShipmentReceiptButton from '../components/ConfirmShipmentReceiptButton'
import ShipmentReferenceFields, {
  shipmentReferencePayload,
  shipmentReferenceValuesFromShipment,
} from '../components/ShipmentReferenceFields'
import useAuthStore from '../store/authStore'
import {
  hasAdminPermission,
  hasAdminRole,
  ADMIN_ROLE_CODES,
  ADMIN_ACTIONS,
  PERMISSION_SUBJECTS,
  canExportData,
} from '../utils/adminPermissions'
import { getArticleCode, getArticleCodeWithLineItems } from '../utils/articleCode'
import { groupOrderLinesForDisplay } from '../utils/groupOrderLineItems'
import { getReconciliationTimelineDetails } from '../utils/orderTimelineDetails'
import {
  getPaymentStatusChipProps,
  getOrderPayableAmount,
} from '../utils/paymentStatusDisplay'
import {
  legacyPaymentProofFiles,
  paymentSummaryWithLegacyPaidOrder,
} from '../utils/paymentProofDisplay'
import {
  getLineMrp,
  getLineSellingPriceTp,
} from '../utils/purchaseOrderLinePricing'
import {
  describeAuditEvent,
  getLinePricingSummary,
  humanizePriceChannel,
  humanizePriceSource,
} from '../utils/priceAuditDisplay'
import {
  CLOSED_WITH_PARTIAL_DELIVERY_LABEL,
  isClosedWithPartialDelivery,
  isDispatchedShipment,
  resolveOrderDetailReturnPath,
  shouldShowPartiallyCompleted,
  shouldShowPartialReceived as shouldShowPartialReceivedForPurchase,
} from '../utils/ordersListUtils'
import ProofOfDelivery from '../components/ProofOfDelivery'
import * as XLSX from 'xlsx'

const ORDER_DETAIL_DETAILED_VIEW_KEY = 'shwapno.orderDetailDetailedView'

/** Fixed DCs whose stock is shown on Order Details line items. */
const LINE_ITEM_DC_STOCK_CODES = ['DK11', 'DK14', 'DK18']

const SIMPLIFIED_EXPORT_HEADERS = [
  'Article Code',
  'Product Name',
  'Quantity',
  'Initial Quantity',
  'DK11 Stock',
  'DK14 Stock',
  'DK18 Stock',
  'MRP',
  'Selling Price',
]

/** Quantity when the line was created (falls back to current qty if unset). */
function getLineInitialQuantityForExport(item) {
  const raw = item?.original_quantity
  if (raw != null && raw !== '' && !Number.isNaN(Number(raw))) {
    return Number(raw)
  }
  return Number(item?.quantity) || 0
}

function mapAvailabilityRows(rows) {
  const map = {}
  for (const row of rows || []) {
    if (row?.order_line_item_id != null) {
      map[row.order_line_item_id] = row
    }
  }
  return map
}

/** Resolve a location by exact code (search API is substring match). */
async function resolveLocationByCode(code) {
  const normalized = String(code || '').trim().toUpperCase()
  if (!normalized) return null
  const data = await locationService.getLocations({
    search: normalized,
    page: 1,
    limit: 50,
  })
  const items = Array.isArray(data?.items) ? data.items : []
  return (
    items.find((loc) => String(loc?.code || '').trim().toUpperCase() === normalized) ||
    null
  )
}

/** Available qty for a display row (supports allocation groups). */
function getLineDcStock(stockByLineId, item) {
  if (!stockByLineId || !item) return null
  const direct = stockByLineId[item.id]
  if (direct) {
    const qty = Number(direct.available_quantity)
    return Number.isFinite(qty) ? qty : 0
  }
  for (const line of item._groupLines || []) {
    const row = stockByLineId[line.id]
    if (row) {
      const qty = Number(row.available_quantity)
      return Number.isFinite(qty) ? qty : 0
    }
  }
  return null
}

function formatDcStockCell(stock, quantity) {
  if (stock == null) return { text: '—', low: false }
  const qty = Number(quantity) || 0
  return { text: String(stock), low: stock < qty }
}

/** RFC 4180 — always quote so Excel does not split on spaces in headers/values. */
function escapeCsvCell(value) {
  const text = value == null ? '' : String(value)
  return `"${text.replace(/"/g, '""')}"`
}

function buildCsvContent(headers, rows) {
  return [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((row) => row.map(escapeCsvCell).join(',')),
  ].join('\r\n')
}

function readDetailedViewPreference() {
  try {
    return localStorage.getItem(ORDER_DETAIL_DETAILED_VIEW_KEY) === 'true'
  } catch {
    return false
  }
}

function persistDetailedViewPreference(enabled) {
  try {
    localStorage.setItem(ORDER_DETAIL_DETAILED_VIEW_KEY, enabled ? 'true' : 'false')
  } catch {
    // ignore quota / private mode
  }
}

function buildSimplifiedCsvRows(lineItems, order, stockByCode = {}) {
  return lineItems.map((item) => {
    const tp = getLineSellingPriceTp(item)
    const mrp = getLineMrp(item)
    const dk11 = getLineDcStock(stockByCode.DK11, item)
    const dk14 = getLineDcStock(stockByCode.DK14, item)
    const dk18 = getLineDcStock(stockByCode.DK18, item)
    return [
      getArticleCode(item) || '',
      item.product_name || '',
      item.quantity || 0,
      getLineInitialQuantityForExport(item),
      dk11 != null ? dk11 : '',
      dk14 != null ? dk14 : '',
      dk18 != null ? dk18 : '',
      mrp != null ? mrp.toFixed(2) : '',
      tp.toFixed(2),
    ]
  })
}

function buildSimplifiedExcelRows(lineItems, order, stockByCode = {}) {
  return lineItems.map((item) => {
    const tp = getLineSellingPriceTp(item)
    const mrp = getLineMrp(item)
    const dk11 = getLineDcStock(stockByCode.DK11, item)
    const dk14 = getLineDcStock(stockByCode.DK14, item)
    const dk18 = getLineDcStock(stockByCode.DK18, item)
    return {
      'Article Code': getArticleCode(item) || '',
      'Product Name': item.product_name || '',
      Quantity: item.quantity || 0,
      'Initial Quantity': getLineInitialQuantityForExport(item),
      'DK11 Stock': dk11 != null ? dk11 : '',
      'DK14 Stock': dk14 != null ? dk14 : '',
      'DK18 Stock': dk18 != null ? dk18 : '',
      MRP: mrp != null ? mrp : '',
      'Selling Price': tp,
    }
  })
}

function buildDetailedCsvRows(lineItems, order, formatDate, stockByCode = {}) {
  const headers = [
    'Line Item ID',
    'Product Name',
    'Article Code',
    'Quantity',
    'Initial Quantity',
    'DK11 Stock',
    'DK14 Stock',
    'DK18 Stock',
    'Received',
    'Returned',
    'Remaining',
    'MRP',
    'Selling Price',
    'Worth Received',
    'Worth Pending',
    'Total Price',
    'Discount Type',
    'Discount Value',
    'Date Created',
    'Date Modified',
  ]

  const rows = lineItems.map((item) => {
    const { received, remaining, damaged, isPurchase } = getLineReceiptColumns(item, order)
    const tp = getLineSellingPriceTp(item)
    const mrp = getLineMrp(item)
    const worthReceived = isPurchase ? getLineWorthReceived(item, order) : ''
    const worthPending = isPurchase ? getLineWorthPending(item, order) : ''
    const dk11 = getLineDcStock(stockByCode.DK11, item)
    const dk14 = getLineDcStock(stockByCode.DK14, item)
    const dk18 = getLineDcStock(stockByCode.DK18, item)
    return [
      item.id || '',
      item.product_name || '',
      getArticleCode(item) || item.sku || '',
      item.quantity || 0,
      getLineInitialQuantityForExport(item),
      dk11 != null ? dk11 : '',
      dk14 != null ? dk14 : '',
      dk18 != null ? dk18 : '',
      isPurchase ? received : '',
      isPurchase ? damaged : '',
      isPurchase ? remaining : '',
      mrp != null ? mrp : '',
      tp,
      worthReceived === '' ? '' : worthReceived,
      worthPending === '' ? '' : worthPending,
      parseFloat(item.total_price || 0),
      item.discount_type || '',
      item.discount_value ? parseFloat(item.discount_value) : '',
      item.date_created ? formatDate(item.date_created, true) : '',
      item.date_modified ? formatDate(item.date_modified, true) : '',
    ]
  })

  return { headers, rows }
}

function buildDetailedExcelRows(lineItems, order, formatDate, stockByCode = {}) {
  return lineItems.map((item) => {
    const { received, remaining, damaged, isPurchase } = getLineReceiptColumns(item, order)
    const tp = getLineSellingPriceTp(item)
    const mrp = getLineMrp(item)
    const worthReceived = isPurchase ? getLineWorthReceived(item, order) : ''
    const worthPending = isPurchase ? getLineWorthPending(item, order) : ''
    const dk11 = getLineDcStock(stockByCode.DK11, item)
    const dk14 = getLineDcStock(stockByCode.DK14, item)
    const dk18 = getLineDcStock(stockByCode.DK18, item)
    return {
      'Line Item ID': item.id || '',
      'Product Name': item.product_name || '',
      'Article Code': getArticleCode(item) || item.sku || '',
      Quantity: item.quantity || 0,
      'Initial Quantity': getLineInitialQuantityForExport(item),
      'DK11 Stock': dk11 != null ? dk11 : '',
      'DK14 Stock': dk14 != null ? dk14 : '',
      'DK18 Stock': dk18 != null ? dk18 : '',
      Received: isPurchase ? received : '',
      Returned: isPurchase ? damaged : '',
      Remaining: isPurchase ? remaining : '',
      MRP: mrp != null ? mrp : '',
      'Selling Price': tp,
      'Worth Received': worthReceived === '' ? '' : worthReceived,
      'Worth Pending': worthPending === '' ? '' : worthPending,
      'Total Price': parseFloat(item.total_price || 0),
      'Discount Type': item.discount_type || '',
      'Discount Value': item.discount_value ? parseFloat(item.discount_value) : '',
      'Date Created': item.date_created ? formatDate(item.date_created, true) : '',
      'Date Modified': item.date_modified ? formatDate(item.date_modified, true) : '',
    }
  })
}

function sanitizeOrderDownloadNamePart(value, fallback = 'unknown') {
  const raw = String(value ?? '').trim()
  if (!raw) return fallback
  return raw
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '_')
}

/** `{last4_org_id}_{org_name}_Order_{order_id}[ _detailed].{csv|xlsx}` */
function getLineItemsDownloadFilename(order, extension, detailed = false) {
  const orgIdStr = String(order.org_id ?? '').replace(/\D/g, '')
  const orgIdSuffix = orgIdStr.slice(-4) || '0000'
  const orgName = sanitizeOrderDownloadNamePart(order.organization_name)
  const orderId = order.order_id ?? order.id ?? 'unknown'
  const detailedSuffix = detailed ? '_detailed' : ''
  return `${orgIdSuffix}_${orgName}_Order_${orderId}${detailedSuffix}.${extension}`
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

/** Display payment-proof datetimes as `dd/mm/yyyy hh:mm:ss` (local time). */
function formatPaymentProofDateTime(value) {
  if (value == null || value === '') return '-'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

const PAYMENT_PROOF_REJECT_REASON_MAX = 500

function formatPaymentMethodLabel(method) {
  const raw = String(method || '').trim()
  if (!raw) return '—'
  if (raw.toUpperCase() === 'BKASH') return 'bKash'
  if (raw.toUpperCase() === 'CASH_DEPOSIT') return 'Cash deposit'
  return raw.replace(/_/g, ' ')
}

function getPaymentProofVerificationChipProps(status) {
  const upper = String(status || '').toUpperCase()
  if (upper === 'VERIFIED') return { label: 'Verified', color: 'success' }
  if (upper === 'REJECTED') return { label: 'Rejected', color: 'error' }
  if (upper === 'PENDING') return { label: 'Pending', color: 'warning' }
  return { label: status || '—', color: 'default' }
}

function getPaymentProofRequestStatusChipProps(status) {
  const upper = String(status || '').toUpperCase()
  if (upper === 'VERIFIED') return { label: 'Verified', color: 'success' }
  if (upper === 'REJECTED') return { label: 'Rejected', color: 'error' }
  if (upper === 'PENDING') return { label: 'Pending', color: 'warning' }
  return { label: status || '—', color: 'default' }
}

function isPaymentProofFilePdf(fileUrl) {
  if (!fileUrl) return false
  const lower = String(fileUrl).toLowerCase()
  return lower.endsWith('.pdf') || lower.includes('application/pdf')
}

function isPaymentProofFileImage(fileUrl) {
  if (!fileUrl || isPaymentProofFilePdf(fileUrl)) return false
  const lower = String(fileUrl).toLowerCase()
  return (
    lower.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i) != null || lower.includes('image/')
  )
}

/**
 * Build readable lines for payment-proof timeline entries (filenames + counts).
 * Prefers timeline meta.payment_proof_datetime; falls back to order-level value
 * for older events recorded before that field was stored on the transition.
 */
const PAYMENT_PROOF_TIMELINE_STATES = new Set([
  'PAYMENT_PROOF_UPLOADED',
  'PAYMENT_PROOF_EDITED',
  'PAYMENT_PROOF_VERIFIED',
  'PAYMENT_PROOF_REJECTED',
  'PAYMENT_PROOF_REVERSED',
])

const getTimelineToStateLabel = (transition, order) => {
  const toState = String(transition?.to_state || '').toUpperCase()
  if (
    toState === 'CLOSED_WITH_PARTIAL_DELIVERY' ||
    transition?.meta?.closed_with_partial_delivery
  ) {
    return CLOSED_WITH_PARTIAL_DELIVERY_LABEL
  }
  if (toState === 'PAYMENT_PROOF_EDITED') {
    return 'Payment Proof Edited'
  }
  if (toState === 'PAYMENT_PROOF_UPLOADED') {
    const reason = String(transition?.reason || '').toLowerCase()
    const meta = transition?.meta || {}
    if (
      reason.includes('payment proof updated') ||
      meta.action === 'edit_after_reject' ||
      meta.edited_after_rejection === true
    ) {
      return 'Payment Proof Edited'
    }
  }
  return getAdminStatusLabel(transition?.to_state, order)
}

/** Lean detail lines for failed assign (payload/response use View APIs). */
const getShipmentCreateFailedTimelineDetails = (transition) => {
  const meta = transition?.meta
  if (!meta || typeof meta !== 'object') return []

  const lines = []
  if (meta.failure_stage) {
    lines.push(`Stage: ${meta.failure_stage}`)
  }
  if (meta.api_http_status != null && meta.api_http_status !== '') {
    lines.push(`API HTTP: ${meta.api_http_status}`)
  }
  if (meta.location_id != null && meta.location_id !== '') {
    lines.push(`Location id: ${meta.location_id}`)
  }
  if (meta.dsd_shipment === true) {
    lines.push(
      meta.vendor_id
        ? `DSD shipment (vendor ${meta.vendor_id})`
        : 'DSD shipment',
    )
  }
  return lines
}

const getPaymentProofTimelineDetails = (transition, fallbackDateTime) => {
  const meta = transition?.meta
  if (!meta || typeof meta !== 'object') return []

  const lines = []
  const isEditAfterReject =
    meta.action === 'edit_after_reject' || meta.edited_after_rejection === true
  const pickedDateTime =
    meta.payment_proof_datetime ||
    (isEditAfterReject ? null : fallbackDateTime)
  if (pickedDateTime) {
    lines.push(
      `Payment proof date & time: ${formatPaymentProofDateTime(pickedDateTime)}`,
    )
  }

  if (meta.payment_proof_request_id != null) {
    lines.push(`Request ID: ${meta.payment_proof_request_id}`)
  }

  if (meta.proof_count != null) {
    lines.push(`Proofs in request: ${meta.proof_count}`)
  }

  if (meta.requested_amount != null) {
    lines.push(`Requested amount: ৳${Number(meta.requested_amount).toFixed(2)}`)
  }

  if (meta.received_amount != null) {
    lines.push(`Received amount: ৳${Number(meta.received_amount).toFixed(2)}`)
  }

  if (meta.rejection_reason) {
    lines.push(`Rejection reason: ${meta.rejection_reason}`)
  }

  if (meta.reversal_reason) {
    lines.push(`Reversal reason: ${meta.reversal_reason}`)
  }

  if (meta.previous_status) {
    lines.push(`Previous status: ${meta.previous_status}`)
  }

  const fileCount = meta.file_count
  const newNames = Array.isArray(meta.new_filenames)
    ? meta.new_filenames.filter(Boolean)
    : []

  if (fileCount != null) {
    lines.push(
      `Total files on order: ${fileCount}`,
    )
  }

  if (newNames.length > 0) {
    lines.push(`Added in this upload: ${newNames.join(', ')}`)
  }

  if (Array.isArray(meta.payment_proof_changes) && meta.payment_proof_changes.length > 0) {
    for (const change of meta.payment_proof_changes) {
      if (change) lines.push(String(change))
    }
  } else if (meta.edit_change_summary) {
    lines.push(String(meta.edit_change_summary))
  }

  return lines
}


/** Product-level leftover closed when admin force-completes a short delivery. */
const getClosedWithPartialDeliveryTimelineDetails = (transition) => {
  const meta = transition?.meta
  if (!meta || typeof meta !== 'object') return []

  const closedLines = Array.isArray(meta.closed_undelivered_lines)
    ? meta.closed_undelivered_lines
    : []
  const lines = []
  for (const item of closedLines) {
    const name =
      item.product_name ||
      (item.order_line_item_id != null ? `Line ${item.order_line_item_id}` : 'Product')
    const closed = item.closed_quantity
    if (closed == null) continue
    const received = item.received_quantity
    const ordered = item.ordered_quantity
    if (ordered != null && received != null) {
      lines.push(
        `${name}: ${closed} closed without adding to stock (received ${received} of ${ordered})`,
      )
    } else {
      lines.push(`${name}: ${closed} closed without adding to stock`)
    }
  }
  return lines
}

/**
 * Map internal order_status to display label (admin view)
 * Admin sees internal order_status directly, not user_status
 * @param {string} orderStatus - Internal order_status value
 * @param {object} [order] - Order row (reserved for purchase-specific label rules)
 * @returns {string} Display label
 */
const getAdminStatusLabel = (orderStatus, _order = null) => {
  if (!orderStatus) return orderStatus || ''

  // Map internal order_status to display labels for admin
  const statusMap = {
    CREATED: 'Created',
    MODIFIED: 'Modified',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    PROCUREMENT_STARTED: 'Procurement Started',
    FULFILLMENT_CENTER_ASSIGNED: 'Fulfillment Center Assigned',
    READY_FOR_DISPATCH: 'Ready for Dispatch',
    DISPATCHED: 'Dispatched',
    RECEIVED_PENDING_VERIFICATION: 'Received (Pending Verification)',
    COMPLETED: 'Completed',
    RETURN_IN_PROGRESS: 'Marked as Returned',
    RETURN_REJECTED: 'Return Rejected',
    RETURN_ACCEPTED: 'Return Accepted',
    RETURN_ACCEPTED_WITH_RECONCILIATIONS: 'Return Accepted with Reconciliations',
    RESOLVED_WITH_CREDIT: 'Resolved with Credit',
    CANCELLED: 'Cancelled',
    FAILED: 'Failed',
    DELIVERED: 'Delivered', // Legacy status
    PAYMENT_PROOF_UPLOADED: 'Payment Proof Uploaded',
    PAYMENT_PROOF_EDITED: 'Payment Proof Edited',
    PAYMENT_PROOF_VERIFIED: 'Payment Proof Verified',
    PAYMENT_PROOF_REJECTED: 'Payment Proof Rejected',
    PAYMENT_PROOF_REVERSED: 'Payment Proof Reversed',
    RETURN_ITEMS_REPORTED: 'Items Returned by Shopkeeper',
    RETURN_SHIPMENT_RECEIVED: 'Return Shipment Received',
    MISSING_ITEMS_RECONCILED: 'Missing Items Reconciled',
    DAMAGED_ITEMS_RECONCILED: 'Damaged Items Reconciled',
    REPLACEMENT_SHIPMENT_DISPATCHED: 'Replacement Shipment Dispatched',
    SHIPMENT_CREATE_FAILED: 'Fulfillment Center Assign Failed',
    CLOSED_WITH_PARTIAL_DELIVERY: CLOSED_WITH_PARTIAL_DELIVERY_LABEL,
  }

  const normalized = String(orderStatus || '').toUpperCase()
  const awaitingWebhook =
    normalized === 'PROCUREMENT_STARTED' &&
    (_order?.shipments || []).some(
      (shipment) =>
        String(shipment?.status || '').toUpperCase() ===
        'FULFILLMENT_CENTER_ASSIGNED',
    )
  if (awaitingWebhook) return 'Fulfillment Center Assigned'

  // Return mapped label if exists, otherwise return order_status as-is
  return statusMap[orderStatus] || orderStatus
}

const shouldShowPartialReceived = (order) =>
  shouldShowPartialReceivedForPurchase(order, false)

/** All lines accounted for with no good units received (only returns). */
const shouldShowFullyReturned = (order) => {
  if (!isPurchaseOrder(order)) return false
  const status = String(order.order_status || '').toUpperCase()
  if (status !== 'COMPLETED' && status !== 'DISPATCHED') return false
  const received = Number(order.total_received_units)
  const remaining = Number(order.total_remaining_units)
  if (!Number.isFinite(received) || !Number.isFinite(remaining)) return false
  if (remaining > 0 || received > 0) return false
  const shipmentReturned = (order.shipments || []).some((s) =>
    String(s.status || '').toUpperCase() === 'FULLY_RETURNED' ||
    (s.items || []).some((i) => Number(i.damaged_quantity) > 0),
  )
  const receiptReturned = (order.receipt_items || []).some(
    (ri) => Number(ri.damaged_quantity) > 0,
  )
  return shipmentReturned || receiptReturned
}

const getAdminDisplayStatusLabel = (order) => {
  if (shouldShowFullyReturned(order)) return 'Returned'
  if (isClosedWithPartialDelivery(order)) return CLOSED_WITH_PARTIAL_DELIVERY_LABEL
  if (shouldShowPartiallyCompleted(order, false)) return 'Partially Completed'
  if (shouldShowPartialReceived(order)) return 'Partially Delivered'
  return getAdminStatusLabel(order?.order_status, order)
}

/** Sequential UI label from render order (1-based); not the database shipment id. */
const getShipmentDisplayLabel = (shipmentDisplayIndex) => {
  if (shipmentDisplayIndex != null && Number(shipmentDisplayIndex) > 0) {
    return `Shipment ${shipmentDisplayIndex}`
  }
  return 'Shipment'
}

/** Format warehouse / DC name or code for a shipment source location. */
const formatLocationLabel = (location) => {
  const name = String(location?.name || location?.location_name || '').trim()
  const code = String(location?.code || location?.location_code || '').trim()
  if (name && code) return `${name} (${code})`
  return name || code || ''
}

/** Warehouse / DC source label for a shipment (supports multi-location ships). */
const getShipmentSourceLabel = (shipment) => {
  const locations = Array.isArray(shipment?.locations)
    ? shipment.locations.filter((loc) => formatLocationLabel(loc))
    : []
  if (locations.length > 0) {
    return locations.map(formatLocationLabel).join(', ')
  }
  return formatLocationLabel({
    name: shipment?.location_name,
    code: shipment?.location_code,
  })
}

/** Purchase shipment status labels on admin (e.g. RECEIVED → Delivered). */
const getPurchaseShipmentStatusLabel = (status) => {
  if (!status) return ''
  const statusMap = {
    RECEIVED: 'Delivered',
    FULLY_RETURNED: 'Returned',
    PARTIALLY_RECEIVED: 'Partially delivered',
    PENDING_RECEIPT: 'Pending receipt',
    DISPATCHED: 'Dispatched',
    FULFILLMENT_CENTER_ASSIGNED: 'Fulfillment Center Assigned',
  }
  return statusMap[status] || String(status).replace(/_/g, ' ')
}

const SALES_ORDER_RECORDING_STATUS = {
  pending: { label: 'Recording pending', color: 'warning' },
  success: { label: 'Recording succeeded', color: 'success' },
  failed: { label: 'Recording failed', color: 'error' },
}

const getSalesOrderRecordingStatus = (shipment) =>
  SALES_ORDER_RECORDING_STATUS[shipment?.sap_sales_order_recording_status] || null

const formatSalesOrderRecordingResponse = (response) => {
  if (response == null || response === '') return 'No response body was stored.'
  if (typeof response === 'string') return response
  try {
    return JSON.stringify(response, null, 2)
  } catch {
    return String(response)
  }
}

/** API may return `PURCHASE` / `Purchase` — normalize for comparisons */
const normalizeOrderType = (orderType) =>
  orderType == null ? '' : String(orderType).toLowerCase()

const isPurchaseOrder = (o) => normalizeOrderType(o?.order_type) === 'purchase'

const getRemainingDamagedToReconcile = (receiptIssues, receiptItemId, damagedQty) => {
  const reconciled =
    receiptIssues?.damaged_reconciled_quantities?.[receiptItemId] || 0
  return Math.max(0, Number(damagedQty) - Number(reconciled))
}

const getRemainingMissingToReconcile = (receiptIssues, receiptItemId, missingQty) => {
  const reconciled =
    receiptIssues?.missing_reconciled_quantities?.[receiptItemId] || 0
  return Math.max(0, Number(missingQty) - Number(reconciled))
}

const countUnreconciledDamagedItems = (receiptIssues) => {
  const items = receiptIssues?.damaged_items || []
  return items.filter(
    (item) =>
      getRemainingDamagedToReconcile(
        receiptIssues,
        item.receipt_item_id,
        item.damaged_quantity,
      ) > 0,
  ).length
}

const countUnreconciledMissingItems = (receiptIssues) => {
  const items = receiptIssues?.missing_items || []
  return items.filter(
    (item) =>
      getRemainingMissingToReconcile(
        receiptIssues,
        item.receipt_item_id,
        item.missing_quantity,
      ) > 0,
  ).length
}

const hasVisibleReceiptIssueActions = (order) => {
  const receiptIssues = order?.meta?.receipt_issues
  if (!receiptIssues) return false

  const missingItems = receiptIssues.missing_items || []
  const damagedItems = receiptIssues.damaged_items || []

  if (missingItems.length === 0 && damagedItems.length === 0) {
    return false
  }

  if (countUnreconciledMissingItems(receiptIssues) > 0) {
    return true
  }

  if (damagedItems.length > 0 && order?.meta?.return_shipment_received !== true) {
    return true
  }

  return countUnreconciledDamagedItems(receiptIssues) > 0
}

function LazySectionMessage({ status, error, onRetry, empty, isEmpty, children }) {
  if (status === 'error') {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error || 'Failed to load this section'}
      </Alert>
    )
  }
  if (status !== 'ready') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }
  if (isEmpty) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {empty}
      </Typography>
    )
  }
  return children
}

/**
 * Purchase lines: ordered qty is `quantity`; backend keeps `remaining_quantity` (outstanding to receive).
 * Received = ordered − remaining. Other order types: not applicable (—).
 */
const getLineReceiptColumns = (item, order) => {
  const ordered = Number(item.quantity) || 0
  if (!isPurchaseOrder(order)) {
    return { ordered, received: null, remaining: null, damaged: null, isPurchase: false }
  }

  const shipments = order.shipments || []
  if (shipments.length > 0) {
    let received = 0
    let damaged = 0
    for (const shipment of shipments) {
      for (const si of shipment.items || []) {
        if (Number(si.order_line_item_id) !== Number(item.id)) continue
        received += Number(si.received_quantity) || 0
        damaged += Number(si.damaged_quantity) || 0
      }
    }
    const remaining = Math.max(0, ordered - received - damaged)
    return { ordered, received, remaining, damaged, isPurchase: true }
  }

  const remRaw = item.remaining_quantity
  const remaining =
    remRaw != null && remRaw !== '' && !Number.isNaN(Number(remRaw))
      ? Math.max(0, Number(remRaw))
      : ordered
  const damagedFromReceipts = (order.receipt_items || [])
    .filter((ri) => String(ri.product_id) === String(item.product_id))
    .reduce((sum, ri) => sum + (Number(ri.damaged_quantity) || 0), 0)
  const received = Math.max(0, ordered - remaining - damagedFromReceipts)
  return {
    ordered,
    received,
    remaining,
    damaged: damagedFromReceipts,
    isPurchase: true,
  }
}

/**
 * Purchase unit cost per line (same precedence as mobile PO detail).
 */
const getLinePurchaseUnitCost = (item) => {
  const p = item?.product || {}
  const raw = parseFloat(
    item?.unit_cost ??
      item?.cost_price ??
      p?.unit_cost ??
      p?.cost_price ??
      item?.unit_price ??
      item?.price ??
      item?.catalog_purchase_unit_cost ??
      0,
  )
  return Number.isFinite(raw) ? raw : 0
}

/** Financial value of good units received into inventory (purchase orders only). */
const getLineWorthReceived = (item, order) => {
  if (!isPurchaseOrder(order)) return null
  const unit = getLinePurchaseUnitCost(item)
  const { received: derivedReceived } = getLineReceiptColumns(item, order)
  const recRaw = item.received_quantity
  const good =
    recRaw != null && recRaw !== '' && !Number.isNaN(Number(recRaw))
      ? Math.max(0, Number(recRaw))
      : typeof derivedReceived === 'number'
        ? derivedReceived
        : 0
  return Math.round(good * unit * 100) / 100
}

/** Units already dispatched to the retailer (from shipment records), per line. */
const getLineShippedQuantity = (item) => {
  const ordered = Number(item.quantity) || 0
  const shippedRaw = item.shipped_quantity
  if (shippedRaw != null && shippedRaw !== '' && !Number.isNaN(Number(shippedRaw))) {
    return Math.max(0, Number(shippedRaw))
  }
  const remShipRaw = item.remaining_to_ship
  if (remShipRaw != null && remShipRaw !== '' && !Number.isNaN(Number(remShipRaw))) {
    return Math.max(0, ordered - Number(remShipRaw))
  }
  return 0
}

/** Grocer-requested / creation snapshot vs current (admin-modified) qty. */
const getLineInitialQuantity = getLineInitialQuantityForExport

const getLineModifiedQuantity = (item) => {
  const raw = item?.modified_quantity
  if (raw != null && raw !== '' && !Number.isNaN(Number(raw))) {
    return Number(raw)
  }
  return Number(item?.quantity) || 0
}

/** Financial value of units shipped to the user (shipped qty × unit cost), purchase orders only. */
const getLineWorthSent = (item, order) => {
  if (!isPurchaseOrder(order)) return null
  const unit = getLinePurchaseUnitCost(item)
  const shipped = getLineShippedQuantity(item)
  return Math.round(shipped * unit * 100) / 100
}

/** Financial value still left to ship (remaining_to_ship × unit cost), purchase orders only. */
const getLineWorthPending = (item, order) => {
  if (!isPurchaseOrder(order)) return null
  const unit = getLinePurchaseUnitCost(item)
  const ordered = Number(item.quantity) || 0
  // Prefer remaining_to_ship so Worth Remaining drops as soon as units are dispatched,
  // not only after grocer receipt updates remaining_quantity (SCRUM-760 / QA).
  const remShipRaw = item.remaining_to_ship
  let pending
  if (remShipRaw != null && remShipRaw !== '' && !Number.isNaN(Number(remShipRaw))) {
    pending = Math.max(0, Number(remShipRaw))
  } else {
    pending = Math.max(0, ordered - getLineShippedQuantity(item))
  }
  return Math.round(Math.max(0, pending) * unit * 100) / 100
}

/** Sum of per-line worth still left to ship (purchase orders only). */
const getOrderTotalWorthRemaining = (order) => {
  if (!isPurchaseOrder(order) || !Array.isArray(order?.order_line_items)) return null
  let sum = 0
  for (const item of order.order_line_items) {
    const w = getLineWorthPending(item, order)
    if (typeof w === 'number' && Number.isFinite(w)) sum += w
  }
  return Math.round(sum * 100) / 100
}

/** Sum of per-line worth shipped to the user (purchase orders only). */
const getOrderTotalWorthSent = (order) => {
  if (!isPurchaseOrder(order) || !Array.isArray(order?.order_line_items)) return null
  let sum = 0
  for (const item of order.order_line_items) {
    const w = getLineWorthSent(item, order)
    if (typeof w === 'number' && Number.isFinite(w)) sum += w
  }
  return Math.round(sum * 100) / 100
}

/** Sum of per-line worth received into inventory (purchase orders only). */
const getOrderTotalWorthReceived = (order) => {
  if (!isPurchaseOrder(order) || !Array.isArray(order?.order_line_items)) return null
  let sum = 0
  for (const item of order.order_line_items) {
    const w = getLineWorthReceived(item, order)
    if (typeof w === 'number' && Number.isFinite(w)) sum += w
  }
  return Math.round(sum * 100) / 100
}

const OrderDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const ordersListPath = resolveOrderDetailReturnPath(
    searchParams,
    location.state,
    '/orders/purchase',
  )
  const handleBackToOrders = useCallback(() => {
    navigate(ordersListPath)
  }, [navigate, ordersListPath])
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false)
  const [previewImageUrl, setPreviewImageUrl] = useState(null)
  const [previewFileName, setPreviewFileName] = useState(null)
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [detailedView, setDetailedView] = useState(() => readDetailedViewPreference())
  const [dcStockByCode, setDcStockByCode] = useState(() =>
    Object.fromEntries(LINE_ITEM_DC_STOCK_CODES.map((code) => [code, {}])),
  )
  const [dcStockLoading, setDcStockLoading] = useState(false)
  const [shipmentReferenceById, setShipmentReferenceById] = useState({})
  const [savingShipmentId, setSavingShipmentId] = useState(null)
  const [shipmentReferenceSaveMessage, setShipmentReferenceSaveMessage] = useState(null)
  const [recordingShipmentId, setRecordingShipmentId] = useState(null)
  const [recordingMessage, setRecordingMessage] = useState(null)
  const [integrationViewKey, setIntegrationViewKey] = useState(null)
  const [recordingResponseView, setRecordingResponseView] = useState(null)
  const [aspenRetryShipmentId, setAspenRetryShipmentId] = useState(null)
  const aspenRetryLock = useRef(new Set())
  const [podToastMessage, setPodToastMessage] = useState(null)
  const [podOperationByShipmentId, setPodOperationByShipmentId] = useState({})
  const podBusyRef = useRef({})
  const [reconcileMissingModalOpen, setReconcileMissingModalOpen] = useState(false)
  const [reconcileDamagedModalOpen, setReconcileDamagedModalOpen] = useState(false)
  const [returnShipmentReceivedLoading, setReturnShipmentReceivedLoading] = useState(false)
  const [reconcileMissingLoading, setReconcileMissingLoading] = useState(false)
  const [reconcileDamagedLoading, setReconcileDamagedLoading] = useState(false)
  const [reconcileMissingData, setReconcileMissingData] = useState({}) // { receipt_item_id: reconcile_quantity }
  const [reconcileDamagedData, setReconcileDamagedData] = useState({}) // { receipt_item_id: reconcile_quantity }

  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const showExcelExports = canExportData(adminPermissions)
  const canForceCompleteShipment = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ORDER,
    ADMIN_ACTIONS.FORCE_COMPLETE_SHIPMENT,
  )
  const [adminProofUploadOpen, setAdminProofUploadOpen] = useState(false)
  const [adminProofFeedback, setAdminProofFeedback] = useState(null)
  const [paymentProofVerifyTarget, setPaymentProofVerifyTarget] = useState(null)
  const [paymentProofEditTarget, setPaymentProofEditTarget] = useState(null)
  const [paymentProofRejectTarget, setPaymentProofRejectTarget] = useState(null)
  const [paymentProofRejectReason, setPaymentProofRejectReason] = useState('')
  const [paymentProofRejectError, setPaymentProofRejectError] = useState(null)
  const [paymentProofRejectSubmitting, setPaymentProofRejectSubmitting] = useState(false)
  const [paymentProofRequestActionId, setPaymentProofRequestActionId] = useState(null)
  const [paymentProofReverseTarget, setPaymentProofReverseTarget] = useState(null)
  const [paymentProofReverseReason, setPaymentProofReverseReason] = useState('')
  const [paymentProofReverseError, setPaymentProofReverseError] = useState(null)
  const [paymentProofReverseSubmitting, setPaymentProofReverseSubmitting] = useState(false)
  const [paymentProofFeedback, setPaymentProofFeedback] = useState(null)
  const [bankAccountBySettingsId, setBankAccountBySettingsId] = useState({})
  const [paymentSectionRequested, setPaymentSectionRequested] = useState(false)
  const paymentRequestedRef = useRef(false)
  const pricingAuditRequestedRef = useRef(false)
  const pricingTimelineRequestedRef = useRef(false)
  const modificationHistoryRequestedRef = useRef(false)
  const timelineRequestedRef = useRef(false)

  const {
    status: paymentStatus,
    data: paymentData,
    error: paymentError,
    ensureLoaded: ensurePaymentLoaded,
  } = useLazyOrderSection(id, orderService.getOrderPayment)
  const {
    status: pricingAuditStatus,
    data: pricingAuditData,
    error: pricingAuditError,
    ensureLoaded: ensurePricingAuditLoaded,
  } = useLazyOrderSection(id, orderService.getOrderPricingAudit)
  const {
    status: pricingTimelineStatus,
    data: pricingTimelineData,
    error: pricingTimelineError,
    ensureLoaded: ensurePricingTimelineLoaded,
  } = useLazyOrderSection(id, orderService.getOrderPricingTimeline)
  const {
    status: modificationHistoryStatus,
    data: modificationHistoryData,
    error: modificationHistoryError,
    ensureLoaded: ensureModificationHistoryLoaded,
  } = useLazyOrderSection(id, orderService.getOrderModificationHistory)
  const {
    status: timelineStatus,
    data: timelineData,
    error: timelineError,
    ensureLoaded: ensureTimelineLoaded,
  } = useLazyOrderSection(id, orderService.getOrderTimeline)

  const ensurePaymentLoadedRef = useRef(ensurePaymentLoaded)
  const ensurePricingAuditLoadedRef = useRef(ensurePricingAuditLoaded)
  const ensurePricingTimelineLoadedRef = useRef(ensurePricingTimelineLoaded)
  const ensureModificationHistoryLoadedRef = useRef(ensureModificationHistoryLoaded)
  const ensureTimelineLoadedRef = useRef(ensureTimelineLoaded)
  ensurePaymentLoadedRef.current = ensurePaymentLoaded
  ensurePricingAuditLoadedRef.current = ensurePricingAuditLoaded
  ensurePricingTimelineLoadedRef.current = ensurePricingTimelineLoaded
  ensureModificationHistoryLoadedRef.current = ensureModificationHistoryLoaded
  ensureTimelineLoadedRef.current = ensureTimelineLoaded

  const refreshOpenedSections = useCallback(async () => {
    const jobs = []
    if (paymentRequestedRef.current) {
      jobs.push(ensurePaymentLoadedRef.current({ force: true }))
    }
    if (pricingAuditRequestedRef.current) {
      jobs.push(ensurePricingAuditLoadedRef.current({ force: true }))
    }
    if (pricingTimelineRequestedRef.current) {
      jobs.push(ensurePricingTimelineLoadedRef.current({ force: true }))
    }
    if (modificationHistoryRequestedRef.current) {
      jobs.push(ensureModificationHistoryLoadedRef.current({ force: true }))
    }
    if (timelineRequestedRef.current) {
      jobs.push(ensureTimelineLoadedRef.current({ force: true }))
    }
    await Promise.all(jobs)
  }, [])

  const fetchOrder = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const response = await orderService.getOrderById(id)
      setOrder(response)
      const refsById = {}
      for (const shipment of response.shipments || []) {
        if (shipment.id) {
          refsById[shipment.id] = shipmentReferenceValuesFromShipment(shipment)
        }
      }
      setShipmentReferenceById(refsById)
      await refreshOpenedSections()
    } catch (err) {
      console.error('Error fetching order:', err)
      setError(err.response?.data?.message || 'Failed to fetch order details')
    } finally {
      setLoading(false)
    }
  }, [id, refreshOpenedSections])

  /** Update product DSD on order lines without a full detail reload. */
  const handleProductDsdUpdated = useCallback((productId, dsd) => {
    const idKey = String(productId || '').trim()
    if (!idKey) return
    setOrder((prev) => {
      if (!prev?.order_line_items?.length) return prev
      let changed = false
      const order_line_items = prev.order_line_items.map((li) => {
        const lineProductId = String(
          li?.admin_product_id || li?.product_id || '',
        ).trim()
        if (lineProductId !== idKey) return li
        if (li.dsd === (dsd === true)) return li
        changed = true
        return { ...li, dsd: dsd === true }
      })
      return changed ? { ...prev, order_line_items } : prev
    })
  }, [])

  const handleProductVatUpdated = useCallback((productId, vat) => {
    const idKey = String(productId || '').trim()
    if (!idKey) return
    setOrder((prev) => {
      if (!prev?.order_line_items?.length) return prev
      let changed = false
      const order_line_items = prev.order_line_items.map((li) => {
        const lineProductId = String(
          li?.admin_product_id || li?.product_id || '',
        ).trim()
        if (lineProductId !== idKey) return li
        if (li.vat === vat) return li
        changed = true
        return { ...li, vat }
      })
      return changed ? { ...prev, order_line_items } : prev
    })
  }, [])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  const {
    balanceChoiceVisible,
    balanceChoiceApplying,
    balanceChoiceError,
    requestOpenPaymentProof,
    closeBalanceChoice,
    confirmUploadProofDeposit,
    proceedWithAvailableBalance,
  } = useAdminPaymentProofUploadGate({
    order,
    onBalanceApplied: async () => {
      setAdminProofFeedback({
        severity: 'success',
        message:
          'Existing balance applied. Procurement can proceed without finance verification.',
      })
      await fetchOrder()
    },
  })

  const requestAdminPaymentProofUpload = useCallback(() => {
    requestOpenPaymentProof(() => setAdminProofUploadOpen(true))
  }, [requestOpenPaymentProof])

  const requestEditRejectedPaymentProof = useCallback(
    (req) => {
      requestOpenPaymentProof(() => setPaymentProofEditTarget(req))
    },
    [requestOpenPaymentProof],
  )

  useEffect(() => {
    const orderId = order?.order_id
    if (!orderId || !order?.order_line_items?.length) {
      setDcStockByCode(
        Object.fromEntries(LINE_ITEM_DC_STOCK_CODES.map((code) => [code, {}])),
      )
      setDcStockLoading(false)
      return
    }

    let cancelled = false
    const loadDcStock = async () => {
      setDcStockLoading(true)
      try {
        const locations = await Promise.all(
          LINE_ITEM_DC_STOCK_CODES.map((code) => resolveLocationByCode(code)),
        )
        if (cancelled) return

        const availabilityResults = await Promise.all(
          locations.map(async (loc) => {
            if (!loc?.id) return {}
            try {
              const res = await adminOrderService.getShipmentAvailability(
                orderId,
                loc.id,
              )
              const payload = res?.data || res
              return mapAvailabilityRows(payload?.lines)
            } catch {
              return {}
            }
          }),
        )
        if (cancelled) return

        const next = {}
        LINE_ITEM_DC_STOCK_CODES.forEach((code, index) => {
          next[code] = availabilityResults[index] || {}
        })
        setDcStockByCode(next)
      } catch (err) {
        console.error('Error fetching DC stock for order line items:', err)
        if (!cancelled) {
          setDcStockByCode(
            Object.fromEntries(LINE_ITEM_DC_STOCK_CODES.map((code) => [code, {}])),
          )
        }
      } finally {
        if (!cancelled) setDcStockLoading(false)
      }
    }

    loadDcStock()
    return () => {
      cancelled = true
    }
  }, [order?.order_id])

  const displayLineItems = useMemo(
    () => groupOrderLinesForDisplay(order?.order_line_items ?? []),
    [order?.order_line_items],
  )
  const initialOrderValue = useMemo(() => {
    const raw = order?.initial_order_value
    if (raw == null || raw === '') return null
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  }, [order?.initial_order_value])

  const canAdminUploadPaymentProof =
    !!order &&
    isPurchaseOrder(order) &&
    String(order.order_status || '').toUpperCase() === 'ACCEPTED' &&
    hasAdminPermission(adminPermissions, 'order', 'startProcurement')

  const paymentProofDetail = paymentData?.payment_proof_detail ?? null
  const hasStructuredPaymentProof = !!order && isPurchaseOrder(order)

  const openPaymentSection = useCallback(
    (expanded) => {
      if (!expanded) return
      paymentRequestedRef.current = true
      setPaymentSectionRequested(true)
      ensurePaymentLoaded()
    },
    [ensurePaymentLoaded],
  )

  const openPricingAuditSection = useCallback(
    (expanded) => {
      if (!expanded) return
      pricingAuditRequestedRef.current = true
      ensurePricingAuditLoaded()
    },
    [ensurePricingAuditLoaded],
  )

  const openPricingTimelineSection = useCallback(
    (expanded) => {
      if (!expanded) return
      pricingTimelineRequestedRef.current = true
      ensurePricingTimelineLoaded()
    },
    [ensurePricingTimelineLoaded],
  )

  const openModificationHistorySection = useCallback(
    (expanded) => {
      if (!expanded) return
      modificationHistoryRequestedRef.current = true
      ensureModificationHistoryLoaded()
    },
    [ensureModificationHistoryLoaded],
  )

  const openTimelineSection = useCallback(
    (expanded) => {
      if (!expanded) return
      timelineRequestedRef.current = true
      ensureTimelineLoaded()
    },
    [ensureTimelineLoaded],
  )

  const canVerifyPaymentProof = hasAdminRole(
    adminRoleCodes,
    ADMIN_ROLE_CODES.PAYMENT_VERIFICATION,
  )
  const canEditRejectedPaymentProof = hasAdminPermission(
    adminPermissions,
    'order',
    'read',
  )

  const sortedStructuredProofs = useMemo(() => {
    const list = paymentProofDetail?.proofs
    if (!Array.isArray(list)) return []
    return [...list].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  }, [paymentProofDetail?.proofs])

  const paymentProofRequestsSorted = useMemo(() => {
    const list = paymentProofDetail?.requests
    if (!Array.isArray(list)) return []
    return [...list].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  }, [paymentProofDetail?.requests])

  const legacyPaymentProofs = useMemo(
    () =>
      legacyPaymentProofFiles(
        {
          payment_proof_datetime: order?.payment_proof_datetime,
          meta: { payment_upload: paymentProofDetail?.payment_upload },
        },
        sortedStructuredProofs,
      ),
    [order?.payment_proof_datetime, paymentProofDetail?.payment_upload, sortedStructuredProofs],
  )

  const paymentSummary = useMemo(
    () =>
      paymentSummaryWithLegacyPaidOrder(
        order,
        paymentProofDetail?.summary,
        legacyPaymentProofs,
      ),
    [order, paymentProofDetail?.summary, legacyPaymentProofs],
  )

  useEffect(() => {
    const requestId = location.state?.editPaymentProofRequestId
    if (!requestId || !id) return
    paymentRequestedRef.current = true
    setPaymentSectionRequested(true)
    ensurePaymentLoaded()
  }, [location.state?.editPaymentProofRequestId, id, ensurePaymentLoaded])

  useEffect(() => {
    const requestId = location.state?.editPaymentProofRequestId
    if (!requestId || !paymentProofRequestsSorted.length) return
    const req = paymentProofRequestsSorted.find((r) => r.id === requestId)
    if (req && String(req.status || '').toUpperCase() === 'REJECTED') {
      setPaymentProofEditTarget(req)
    }
  }, [location.state?.editPaymentProofRequestId, paymentProofRequestsSorted])

  useEffect(() => {
    if (!order?.order_id || !isPurchaseOrder(order) || !paymentSectionRequested) {
      return undefined
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await adminOrderService.getPaymentProofReceivingAccounts()
        if (cancelled) return
        const accounts = res?.data ?? []
        const map = {}
        for (const account of accounts) {
          if (account?.id != null) map[String(account.id)] = account
        }
        setBankAccountBySettingsId(map)
      } catch (err) {
        console.error('Failed to load Shwapno bank accounts for payment proof:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [order?.order_id, order?.order_type, paymentSectionRequested])

  const shwapnoReceivingAccounts = useMemo(
    () => Object.values(bankAccountBySettingsId || {}),
    [bankAccountBySettingsId],
  )

  const openPaymentProofPreview = useCallback((fileUrl, fileName) => {
    if (!fileUrl) return
    setPreviewImageUrl(fileUrl)
    setPreviewFileName(fileName || 'Payment proof')
    setImagePreviewOpen(true)
  }, [])

  const closePaymentProofRejectDialog = useCallback(() => {
    if (paymentProofRejectSubmitting) return
    setPaymentProofRejectTarget(null)
    setPaymentProofRejectReason('')
    setPaymentProofRejectError(null)
  }, [paymentProofRejectSubmitting])

  const closePaymentProofReverseDialog = useCallback(() => {
    if (paymentProofReverseSubmitting) return
    setPaymentProofReverseTarget(null)
    setPaymentProofReverseReason('')
    setPaymentProofReverseError(null)
  }, [paymentProofReverseSubmitting])

  const handleConfirmReversePaymentProofRequest = async () => {
    if (!order?.order_id || !paymentProofReverseTarget?.id) return
    const reason = paymentProofReverseReason.trim()
    if (!reason) {
      setPaymentProofReverseError('Reversal reason is required.')
      return
    }
    try {
      setPaymentProofReverseSubmitting(true)
      setPaymentProofReverseError(null)
      await adminOrderService.reversePaymentProofRequest(
        order.order_id,
        paymentProofReverseTarget.id,
        reason,
      )
      setPaymentProofReverseTarget(null)
      setPaymentProofReverseReason('')
      setPaymentProofFeedback({
        severity: 'success',
        message: 'Payment proof request reversed to pending.',
      })
      await fetchOrder()
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to reverse payment proof request'
      setPaymentProofReverseError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofReverseSubmitting(false)
    }
  }

  const handleVerifyPaymentProofRequest = (requestId) => {
    const req = paymentProofRequestsSorted.find((r) => r.id === requestId)
    if (!req) return
    setPaymentProofVerifyTarget(req)
  }

  const handleConfirmVerifyPaymentProofRequest = async (payload) => {
    if (!order?.order_id || !paymentProofVerifyTarget?.id) return
    try {
      setPaymentProofRequestActionId(paymentProofVerifyTarget.id)
      setPaymentProofFeedback(null)
      await adminOrderService.verifyPaymentProofRequest(
        order.order_id,
        paymentProofVerifyTarget.id,
        payload,
      )
      setPaymentProofVerifyTarget(null)
      setPaymentProofFeedback({ severity: 'success', message: 'Payment proof request verified.' })
      await fetchOrder()
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to verify payment proof request')
      throw new Error(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofRequestActionId(null)
    }
  }

  const handleConfirmRejectPaymentProofRequest = async () => {
    if (!order?.order_id || !paymentProofRejectTarget?.id) return
    const reason = paymentProofRejectReason.trim()
    if (!reason) {
      setPaymentProofRejectError('Rejection reason is required.')
      return
    }
    try {
      setPaymentProofRejectSubmitting(true)
      setPaymentProofRejectError(null)
      await adminOrderService.rejectPaymentProofRequest(
        order.order_id,
        paymentProofRejectTarget.id,
        reason,
      )
      closePaymentProofRejectDialog()
      setPaymentProofFeedback({ severity: 'success', message: 'Payment proof request rejected.' })
      await fetchOrder()
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to reject payment proof request'
      setPaymentProofRejectError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofRejectSubmitting(false)
    }
  }

  /**
   * Get status color for admin view (uses internal order_status)
   * @param {string} orderStatus - Internal order_status value
   * @returns {string} MUI color name
   */
  const getStatusColor = (orderStatus, orderForPartial) => {
    if (orderForPartial && isClosedWithPartialDelivery(orderForPartial)) {
      return 'warning'
    }
    if (orderForPartial && shouldShowPartiallyCompleted(orderForPartial, false)) {
      return 'warning'
    }
    if (orderForPartial && shouldShowPartialReceived(orderForPartial)) {
      return 'warning'
    }
    if (!orderStatus) return 'default'

    // Map internal order_status to MUI colors
    const statusColors = {
      CREATED: 'default',
      MODIFIED: 'warning',
      ACCEPTED: 'info',
      REJECTED: 'error',
      PROCUREMENT_STARTED: 'warning',
      FULFILLMENT_CENTER_ASSIGNED: 'warning',
      READY_FOR_DISPATCH: 'warning',
      DISPATCHED: 'info',
      DELIVERED: 'success',
      CANCELLED: 'error',
      FAILED: 'error',
      COMPLETED: 'success',
      RECEIVED_PENDING_VERIFICATION: 'warning',
      RETURN_IN_PROGRESS: 'warning',
      RETURN_REJECTED: 'error',
      RETURN_ACCEPTED: 'success',
      RETURN_ACCEPTED_WITH_RECONCILIATIONS: 'success',
      RESOLVED_WITH_CREDIT: 'success',
      PAYMENT_PROOF_UPLOADED: 'info',
      PAYMENT_PROOF_EDITED: 'info',
      PAYMENT_PROOF_VERIFIED: 'success',
      PAYMENT_PROOF_REJECTED: 'error',
      PAYMENT_PROOF_REVERSED: 'warning',
      RETURN_ITEMS_REPORTED: 'warning',
      RETURN_SHIPMENT_RECEIVED: 'info',
      MISSING_ITEMS_RECONCILED: 'info',
      DAMAGED_ITEMS_RECONCILED: 'info',
      REPLACEMENT_SHIPMENT_DISPATCHED: 'primary',
      SHIPMENT_CREATE_FAILED: 'error',
      CLOSED_WITH_PARTIAL_DELIVERY: 'warning',
    }
    return statusColors[orderStatus] || 'default'
  }

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      if (includeTime) {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      }
    } catch (err) {
      return dateString
    }
  }

  const formatAmount = (amount) => {
    if (typeof amount === 'number') {
      return `৳${amount.toFixed(2)}`
    }
    return `৳${parseFloat(amount || 0).toFixed(2)}`
  }

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget)
  }

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null)
  }

  const openIntegrationView = async (kind, shipment, shipmentLabel) => {
    if (!id || !shipment?.id || integrationViewKey) return
    const key = `${kind}:${shipment.id}`
    setIntegrationViewKey(key)
    try {
      if (kind === 'recording') {
        const data = await adminOrderService.getSalesOrderRecording(id, shipment.id)
        setRecordingResponseView({
          heading: `${shipmentLabel} recording response`,
          response: data?.response ?? null,
        })
      } else if (kind === 'sap-order') {
        const data = await adminOrderService.getSapSalesOrderPayload(id, shipment.id)
        setRecordingResponseView({
          heading: `${shipmentLabel} SAP Order Payload`,
          response: data?.request ?? null,
        })
      } else if (kind === 'webhook') {
        const data = await adminOrderService.getPurchaseOrderUpdate(id, shipment.id)
        setRecordingResponseView({
          heading: `${shipmentLabel} webhook`,
          response: data,
        })
      } else {
        const data = await adminOrderService.getAspenReceiveItems(id, shipment.id)
        setRecordingResponseView({
          heading: `${shipmentLabel} Shwapno Receive Item`,
          response: data?.response != null ? data.response : data?.error || null,
        })
      }
    } catch (err) {
      setRecordingMessage(getApiErrorMessage(err, 'Could not load the details'))
    } finally {
      setIntegrationViewKey(null)
    }
  }

  /** View payload / whole response for a SHIPMENT_CREATE_FAILED timeline row. */
  const openShipmentCreateFailureView = async (kind, transition) => {
    if (!id || !transition?.id || integrationViewKey) return
    const key = `create-fail-${kind}:${transition.id}`
    setIntegrationViewKey(key)
    try {
      if (kind === 'request') {
        const data = await adminOrderService.getShipmentCreateFailureRequest(
          id,
          transition.id,
        )
        setRecordingResponseView({
          heading: 'SAP Order Payload — exact request sent to API',
          response: data?.request ?? null,
        })
      } else {
        const data = await adminOrderService.getShipmentCreateFailureResponse(
          id,
          transition.id,
        )
        setRecordingResponseView({
          heading: 'SAP create response (whole API response)',
          response: data?.response ?? null,
        })
      }
    } catch (err) {
      setRecordingMessage(getApiErrorMessage(err, 'Could not load the details'))
    } finally {
      setIntegrationViewKey(null)
    }
  }

  const handleRetrySalesOrderRecording = async (shipmentId) => {
    if (!id || !shipmentId || !isSuperuserActor) return
    try {
      setRecordingShipmentId(shipmentId)
      setRecordingMessage(null)
      const response = await adminOrderService.retrySalesOrderRecording(id, shipmentId)
      const updated = response?.shipment
      if (updated?.id) {
        setOrder((prev) => {
          if (!prev?.shipments) return prev
          return {
            ...prev,
            shipments: prev.shipments.map((shipment) =>
              shipment.id === updated.id ? { ...shipment, ...updated } : shipment,
            ),
          }
        })
      }
      setRecordingMessage(
        response?.message ||
          (updated?.sap_sales_order_recording_status === 'success'
            ? 'Sales order recorded'
            : 'Sales order recording failed'),
      )
    } catch (err) {
      console.error('Error retrying sales order recording:', err)
      const message = err.response?.data?.message || 'Sales order recording failed'
      if (err.response?.status === 409) {
        try {
          const fresh = await orderService.getOrderById(id)
          setOrder(fresh)
        } catch (refreshErr) {
          console.error('Error refreshing order after recording conflict:', refreshErr)
        }
      }
      setRecordingMessage(Array.isArray(message) ? message.join(', ') : message)
    } finally {
      setRecordingShipmentId(null)
    }
  }

  const handleRetryAspenReceiveItems = async (shipmentId) => {
    if (!id || !shipmentId || !isSuperuserActor) return
    if (aspenRetryLock.current.has(shipmentId)) return
    aspenRetryLock.current.add(shipmentId)
    setAspenRetryShipmentId(shipmentId)
    setRecordingMessage(null)
    try {
      const response = await adminOrderService.retryAspenReceiveItems(id, shipmentId)
      setOrder((prev) => {
        if (!prev?.shipments) return prev
        return {
          ...prev,
          shipments: prev.shipments.map((shipment) =>
            shipment.id === shipmentId
              ? {
                  ...shipment,
                  aspen_receive_items_status: response?.status ?? shipment.aspen_receive_items_status,
                  aspen_receive_items_response:
                    response?.aspen_receive_items_response ?? shipment.aspen_receive_items_response,
                }
              : shipment,
          ),
        }
      })
      setRecordingMessage(response?.message || 'Shwapno receive item failed')
    } catch (err) {
      console.error('Error retrying Shwapno receive item:', err)
      const message = err.response?.data?.message || 'Shwapno receive item failed'
      setRecordingMessage(Array.isArray(message) ? message.join(', ') : message)
    } finally {
      aspenRetryLock.current.delete(shipmentId)
      setAspenRetryShipmentId(null)
    }
  }

  const handleSaveShipmentReferences = async (shipmentId) => {
    if (!id || !shipmentId) return
    const values = shipmentReferenceById[shipmentId]
    if (!values) return

    try {
      setSavingShipmentId(shipmentId)
      setShipmentReferenceSaveMessage(null)
      await adminOrderService.updateShipmentReferenceFields(
        id,
        shipmentId,
        shipmentReferencePayload(values),
      )
      setShipmentReferenceSaveMessage('Shipment reference numbers saved successfully')
      await fetchOrder()
      setTimeout(() => setShipmentReferenceSaveMessage(null), 3000)
    } catch (err) {
      console.error('Error saving shipment references:', err)
      setError(err.response?.data?.message || 'Failed to save shipment reference numbers')
    } finally {
      setSavingShipmentId(null)
    }
  }

  const patchShipmentProofOfDelivery = (shipmentId, files) => {
    setOrder((prev) => {
      if (!prev?.shipments) return prev
      return {
        ...prev,
        shipments: prev.shipments.map((shipment) =>
          shipment.id === shipmentId
            ? { ...shipment, proof_of_delivery: files }
            : shipment,
        ),
      }
    })
  }

  const runShipmentPodOperation = async (shipmentId, mode, operation) => {
    if (podBusyRef.current[shipmentId]) return
    podBusyRef.current[shipmentId] = true
    setPodOperationByShipmentId((prev) => ({ ...prev, [shipmentId]: mode }))
    try {
      await operation()
    } finally {
      podBusyRef.current[shipmentId] = false
      setPodOperationByShipmentId((prev) => {
        const next = { ...prev }
        delete next[shipmentId]
        return next
      })
    }
  }

  const handleUploadProofOfDelivery = (shipmentId, files) =>
    runShipmentPodOperation(shipmentId, 'upload', async () => {
      try {
        const response = await adminOrderService.uploadShipmentProofOfDelivery(id, shipmentId, files)
        patchShipmentProofOfDelivery(shipmentId, response?.proof_of_delivery || [])
        setPodToastMessage('Proof of delivery uploaded')
        setTimeout(() => setPodToastMessage(null), 3000)
      } catch (err) {
        console.error('Error uploading proof of delivery:', err)
        const message = err.response?.data?.message || 'Failed to upload proof of delivery'
        setError(Array.isArray(message) ? message.join(', ') : message)
      }
    })

  const handleDeleteProofOfDelivery = (shipmentId, fileId) =>
    runShipmentPodOperation(shipmentId, 'delete', async () => {
      try {
        const response = await adminOrderService.deleteShipmentProofOfDelivery(
          id,
          shipmentId,
          fileId,
        )
        patchShipmentProofOfDelivery(shipmentId, response?.proof_of_delivery || [])
        setPodToastMessage('Proof of delivery file deleted')
        setTimeout(() => setPodToastMessage(null), 3000)
      } catch (err) {
        console.error('Error deleting proof of delivery:', err)
        const message = err.response?.data?.message || 'Failed to delete proof of delivery'
        setError(Array.isArray(message) ? message.join(', ') : message)
      }
    })

  const handleMarkReturnShipmentReceived = async () => {
    if (!id) return
    try {
      setReturnShipmentReceivedLoading(true)
      await adminOrderService.markReturnShipmentReceived(id)
      await fetchOrder()
      setError(null)
    } catch (err) {
      console.error('Error marking return shipment as received:', err)
      setError(err.response?.data?.message || 'Failed to mark return shipment as received')
    } finally {
      setReturnShipmentReceivedLoading(false)
    }
  }

  const handleOpenReconcileMissing = () => {
    const receiptIssues = order?.meta?.receipt_issues
    const missingItems = receiptIssues?.missing_items || []
    const initialData = {}
    missingItems.forEach((item) => {
      const remaining = getRemainingMissingToReconcile(
        receiptIssues,
        item.receipt_item_id,
        item.missing_quantity,
      )
      if (remaining > 0) {
        initialData[item.receipt_item_id] = remaining
      }
    })
    setReconcileMissingData(initialData)
    setReconcileMissingModalOpen(true)
  }

  const handleOpenReconcileDamaged = () => {
    const receiptIssues = order?.meta?.receipt_issues
    const damagedItems = receiptIssues?.damaged_items || []
    const initialData = {}
    damagedItems.forEach((item) => {
      const remaining = getRemainingDamagedToReconcile(
        receiptIssues,
        item.receipt_item_id,
        item.damaged_quantity,
      )
      if (remaining > 0) {
        initialData[item.receipt_item_id] = remaining
      }
    })
    setReconcileDamagedData(initialData)
    setReconcileDamagedModalOpen(true)
  }

  const handleReconcileMissing = async () => {
    if (!id) return
    try {
      setReconcileMissingLoading(true)
      const receiptIssues = order?.meta?.receipt_issues
      const missingItems = receiptIssues?.missing_items || []
      const reconcileItems = missingItems
        .map((item) => {
          const remaining = getRemainingMissingToReconcile(
            receiptIssues,
            item.receipt_item_id,
            item.missing_quantity,
          )
          const qty = Math.min(
            remaining,
            Number(reconcileMissingData[item.receipt_item_id]) || 0,
          )
          return {
            receipt_item_id: item.receipt_item_id,
            product_id: item.product_id,
            reconcile_quantity: qty,
          }
        })
        .filter((item) => item.reconcile_quantity > 0)
      
      if (reconcileItems.length === 0) {
        setError('Please select at least one item to reconcile')
        return
      }

      await adminOrderService.reconcileMissingItems(id, reconcileItems)
      await fetchOrder()
      setReconcileMissingModalOpen(false)
      setReconcileMissingData({})
      setError(null)
    } catch (err) {
      console.error('Error reconciling missing items:', err)
      setError(err.response?.data?.message || 'Failed to reconcile missing items')
    } finally {
      setReconcileMissingLoading(false)
    }
  }

  const handleReconcileDamaged = async () => {
    if (!id) return
    try {
      setReconcileDamagedLoading(true)
      const receiptIssues = order?.meta?.receipt_issues
      const damagedItems = receiptIssues?.damaged_items || []
      const reconcileItems = damagedItems
        .map((item) => {
          const remaining = getRemainingDamagedToReconcile(
            receiptIssues,
            item.receipt_item_id,
            item.damaged_quantity,
          )
          const qty = Math.min(
            remaining,
            Number(reconcileDamagedData[item.receipt_item_id]) || 0,
          )
          return {
            receipt_item_id: item.receipt_item_id,
            product_id: item.product_id,
            reconcile_quantity: qty,
          }
        })
        .filter((item) => item.reconcile_quantity > 0)
      
      if (reconcileItems.length === 0) {
        setError('Please select at least one item to reconcile')
        return
      }

      await adminOrderService.reconcileDamagedItems(id, reconcileItems)
      await fetchOrder()
      setReconcileDamagedModalOpen(false)
      setReconcileDamagedData({})
      setError(null)
    } catch (err) {
      console.error('Error reconciling damaged items:', err)
      setError(err.response?.data?.message || 'Failed to reconcile damaged items')
    } finally {
      setReconcileDamagedLoading(false)
    }
  }

  const handleDetailedViewChange = (event) => {
    const enabled = event.target.checked
    setDetailedView(enabled)
    persistDetailedViewPreference(enabled)
  }

  const handleDownloadCSV = () => {
    if (!order || !order.order_line_items || order.order_line_items.length === 0) {
      alert('No line items to download')
      handleDownloadMenuClose()
      return
    }

    try {
      setDownloading(true)

      const lineItems = order.order_line_items
      const { headers, rows } = detailedView
        ? buildDetailedCsvRows(lineItems, order, formatDate, dcStockByCode)
        : {
            headers: SIMPLIFIED_EXPORT_HEADERS,
            rows: buildSimplifiedCsvRows(lineItems, order, dcStockByCode),
          }

      const csv = buildCsvContent(headers, rows)

      const csvFilename = getLineItemsDownloadFilename(order, 'csv', detailedView)

      // UTF-8 BOM helps Excel open quoted columns correctly.
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = csvFilename
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)

      handleDownloadMenuClose()
    } catch (err) {
      console.error('Error downloading CSV:', err)
      alert('Failed to download CSV: ' + (err.message || 'Unknown error'))
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadExcel = () => {
    if (!order || !order.order_line_items || order.order_line_items.length === 0) {
      alert('No line items to download')
      handleDownloadMenuClose()
      return
    }

    try {
      setDownloading(true)

      const lineItems = order.order_line_items
      const data = detailedView
        ? buildDetailedExcelRows(lineItems, order, formatDate, dcStockByCode)
        : buildSimplifiedExcelRows(lineItems, order, dcStockByCode)

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(data)

      ws['!cols'] = detailedView
        ? [
            { wch: 12 },
            { wch: 30 },
            { wch: 15 },
            { wch: 10 },
            { wch: 14 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 10 },
            { wch: 10 },
            { wch: 12 },
            { wch: 14 },
            { wch: 14 },
            { wch: 14 },
            { wch: 14 },
            { wch: 12 },
            { wch: 18 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
          ]
        : [
            { wch: 15 },
            { wch: 30 },
            { wch: 12 },
            { wch: 14 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 14 },
          ]

      XLSX.utils.book_append_sheet(wb, ws, 'Line Items')

      const filename = getLineItemsDownloadFilename(order, 'xlsx', detailedView)
      // Generate Excel file and download
      XLSX.writeFile(wb, filename)

      handleDownloadMenuClose()
    } catch (err) {
      console.error('Error downloading Excel:', err)
      alert('Failed to download Excel: ' + (err.message || 'Unknown error'))
    } finally {
      setDownloading(false)
    }
  }

  const handleSuperadminExport = async (format) => {
    if (!id) return
    handleDownloadMenuClose()
    try {
      setDownloading(true)
      // Async job (same worker path as bulk PO export). File is not generated in the API.
      const response = await adminOrderService.exportPurchaseOrder(id, format)
      navigate('/orders/purchase/exports', {
        state: {
          queuedMessage: response?.jobId
            ? `Export started (Job ID ${response.jobId}). Watching for progress…`
            : response?.message || 'Export started. Watching for progress…',
        },
      })
    } catch (err) {
      console.error(`Error exporting purchase order as ${format}:`, err)
      alert(
        getApiErrorMessage(
          err,
          `Failed to start ${format === 'csv' ? 'CSV' : 'Excel'} export`,
        ),
      )
    } finally {
      setDownloading(false)
    }
  }

  const paymentStatusChip = getPaymentStatusChipProps(
    paymentProofDetail
      ? { ...order, payment_proof_detail: paymentProofDetail }
      : order,
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToOrders}
          sx={{ mb: 2 }}
        >
          Back to Orders
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!order) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToOrders}
          sx={{ mb: 2 }}
        >
          Back to Orders
        </Button>
        <Alert severity="info">Order not found</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToOrders}
            variant="outlined"
          >
            Back to Orders
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
              Order #{order.order_id}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Order Details
            </Typography>
          </Box>
        </Box>
        {((order.order_line_items && order.order_line_items.length > 0) ||
          (showExcelExports && isPurchaseOrder(order))) && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={handleDownloadMenuOpen}
            disabled={downloading}
            sx={{ bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' } }}
          >
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
        )}
      </Box>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
        slotProps={{ paper: { sx: { minWidth: 360 } } }}
      >
        {order.order_line_items && order.order_line_items.length > 0 && (
          <>
            <ListSubheader sx={{ lineHeight: 1.4, py: 1.25 }}>
              Line items (this page)
            </ListSubheader>
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Current line items as shown on this order: article, quantity,
                Initial Quantity, MRP, and selling price.
              </Typography>
            </Box>
            <Box
              sx={{ px: 2, pb: 1 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={detailedView}
                    onChange={handleDetailedViewChange}
                    size="small"
                    color="primary"
                  />
                }
                label="Detailed view"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, pl: 4.5 }}>
                {detailedView
                  ? 'Includes all table columns (stock, discounts, and pricing details).'
                  : 'Columns: Article Code, Product Name, Quantity, Initial Quantity, DK11/DK14/DK18 stock, MRP, Selling Price.'}
              </Typography>
            </Box>
            <MenuItem onClick={handleDownloadCSV} disabled={downloading}>
              <ListItemIcon>
                <TableChartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Download line items CSV"
                secondary="Matches the table on this page"
              />
            </MenuItem>
            {showExcelExports ? (
              <MenuItem onClick={handleDownloadExcel} disabled={downloading}>
                <ListItemIcon>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Download line items Excel"
                  secondary="Matches the table on this page"
                />
              </MenuItem>
            ) : null}
          </>
        )}
        {showExcelExports && isPurchaseOrder(order) && (
          <>
            {order.order_line_items && order.order_line_items.length > 0 && (
              <Divider />
            )}
            <ListSubheader sx={{ lineHeight: 1.4, py: 1.25 }}>
              Original vs modified quantities
            </ListSubheader>
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Original and last-modified quantity, selling price, and MRP,
                plus who last changed the quantity.
              </Typography>
            </Box>
            <MenuItem
              onClick={() => handleSuperadminExport('xlsx')}
              disabled={downloading}
            >
              <ListItemIcon>
                <FileDownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Export modification report Excel"
                secondary="Queues a background export — download when Ready"
              />
            </MenuItem>
            <MenuItem
              onClick={() => handleSuperadminExport('csv')}
              disabled={downloading}
            >
              <ListItemIcon>
                <TableChartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Export modification report CSV"
                secondary="Queues a background export — download when Ready"
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Order Actions Panel */}
      <OrderActionsPanel
        order={order}
        onOrderUpdated={fetchOrder}
        onProductDsdUpdated={handleProductDsdUpdated}
        onProductVatUpdated={handleProductVatUpdated}
        canEnterManualSo={canForceCompleteShipment}
      />

      {isPurchaseOrder(order) && order.shipments?.length > 0 && (
        <CollapsibleSection title="Shipment history">
          {order.shipments.map((shipment, index) => (
            <Box
              key={shipment.id ?? shipment.shipment_number}
              sx={{
                mb: 2,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#fafafa',
                '&:last-child': { mb: 0 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {getShipmentDisplayLabel(index + 1)}
                    {shipment.legacy ? ' (legacy)' : ''} —{' '}
                    {getPurchaseShipmentStatusLabel(shipment.status)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {shipment.dispatched_at
                      ? formatDate(shipment.dispatched_at)
                      : ''}
                  </Typography>
                  {getShipmentSourceLabel(shipment) ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Dispatched from: {getShipmentSourceLabel(shipment)}
                    </Typography>
                  ) : null}
                  {/*
                    TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
                    Hide SAP/Aspen/manual-webhook actions for local-only snapshots.
                    After rollout, unwrap and always render this block.
                  */}
                  {shipment.external_integration_enabled !== false ? (
                    <>
                      {shipment.sap_sales_document ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          SAP sales document: {shipment.sap_sales_document}
                          {shipment.sap_so_creation_source === 'MANUAL'
                            ? ' (manual)'
                            : shipment.sap_so_creation_source === 'SYSTEM'
                              ? ' (system)'
                              : ''}
                        </Typography>
                      ) : null}
                      {shipment.sap_so_creation_source === 'MANUAL' &&
                      shipment.sap_so_manual_reason ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          Manual SO reason: {shipment.sap_so_manual_reason}
                        </Typography>
                      ) : null}
                      <SapSalesOrderStatus
                        visible={
                          shipment.sap_so_creation_source === 'SYSTEM' &&
                          Boolean(shipment.sap_sales_document)
                        }
                        viewLoading={integrationViewKey === `sap-order:${shipment.id}`}
                        onRequestView={() =>
                          openIntegrationView(
                            'sap-order',
                            shipment,
                            getShipmentDisplayLabel(index + 1),
                          )
                        }
                      />
                      {(() => {
                        const recordingStatus = getSalesOrderRecordingStatus(shipment)
                        if (!recordingStatus) return null
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={recordingStatus.label}
                              color={recordingStatus.color}
                            />
                            {shipment.sap_sales_order_recording_status === 'success' ||
                            shipment.sap_sales_order_recording_status === 'failed' ? (
                              <IconButton
                                size="small"
                                aria-label="View recording response"
                                disabled={integrationViewKey === `recording:${shipment.id}`}
                                onClick={() =>
                                  openIntegrationView(
                                    'recording',
                                    shipment,
                                    getShipmentDisplayLabel(index + 1),
                                  )
                                }
                              >
                                {integrationViewKey === `recording:${shipment.id}` ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : null}
                            {isSuperuserActor &&
                            shipment.sap_sales_order_recording_status === 'failed' &&
                            shipment.id ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={recordingShipmentId === shipment.id}
                                onClick={() => handleRetrySalesOrderRecording(shipment.id)}
                              >
                                {recordingShipmentId === shipment.id
                                  ? 'Recording…'
                                  : 'Retry recording'}
                              </Button>
                            ) : null}
                          </Box>
                        )
                      })()}
                      <AspenReceiveItemsStatus
                        status={shipment.aspen_receive_items_status}
                        response={null}
                        shipmentLabel={getShipmentDisplayLabel(index + 1)}
                        isSuperuser={isSuperuserActor}
                        retrying={aspenRetryShipmentId === shipment.id}
                        onRetry={() => handleRetryAspenReceiveItems(shipment.id)}
                        onView={setRecordingResponseView}
                        onRequestView={() =>
                          openIntegrationView(
                            'aspen',
                            shipment,
                            getShipmentDisplayLabel(index + 1),
                          )
                        }
                        viewLoading={integrationViewKey === `aspen:${shipment.id}`}
                      />
                      <PurchaseOrderUpdateStatus
                        status={shipment.purchase_order_update_status}
                        update={shipment.purchase_order_update}
                        shipmentLabel={getShipmentDisplayLabel(index + 1)}
                        onView={setRecordingResponseView}
                        onRequestView={() =>
                          openIntegrationView(
                            'webhook',
                            shipment,
                            getShipmentDisplayLabel(index + 1),
                          )
                        }
                        viewLoading={integrationViewKey === `webhook:${shipment.id}`}
                      />
                      <ManualWebhookDialog
                        orderId={id}
                        shipment={shipment}
                        canSubmit={
                          canForceCompleteShipment &&
                          Boolean(shipment.can_submit_manual_webhook)
                        }
                        onRefresh={fetchOrder}
                      />
                    </>
                  ) : null}
                </Box>
                {isDispatchedShipment(shipment) && (
                  <ProofOfDelivery
                    shipmentId={shipment.id}
                    files={
                      Array.isArray(shipment.proof_of_delivery)
                        ? shipment.proof_of_delivery
                        : []
                    }
                    disabled={Boolean(podOperationByShipmentId[shipment.id])}
                    uploading={podOperationByShipmentId[shipment.id] === 'upload'}
                    onUpload={handleUploadProofOfDelivery}
                    onDelete={handleDeleteProofOfDelivery}
                  />
                )}
              </Box>
              {!shipment.legacy && shipment.id ? (
                <Box sx={{ mt: 1.5 }}>
                  <ShipmentReferenceFields
                    values={
                      shipmentReferenceById[shipment.id] ||
                      shipmentReferenceValuesFromShipment(shipment)
                    }
                    onChange={(next) =>
                      setShipmentReferenceById((prev) => ({
                        ...prev,
                        [shipment.id]: next,
                      }))
                    }
                    onSave={() => handleSaveShipmentReferences(shipment.id)}
                    saving={savingShipmentId === shipment.id}
                    disabled={savingShipmentId != null && savingShipmentId !== shipment.id}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  <ShipmentReferenceFields
                    values={shipmentReferenceValuesFromShipment(shipment)}
                    disabled
                  />
                </Box>
              )}
              {shipment.items?.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {shipment.items.map((si) => {
                    const articleCode = getArticleCode(si)
                    return (
                    <li key={si.order_line_item_id}>
                      <Typography variant="body2">
                        {si.product_name || 'Product'}: shipped {si.quantity},
                        received {si.received_quantity ?? 0},
                        {Number(si.damaged_quantity) > 0
                          ? `, returned/damaged ${si.damaged_quantity}`
                          : ''}
                        , remaining {si.remaining_to_receive ?? 0}
                      </Typography>
                      {articleCode && (
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Article Code: {articleCode}
                        </Typography>
                      )}
                    </li>
                    )
                  })}
                </Box>
              )}
              {canForceCompleteShipment &&
                isPurchaseOrder(order) &&
                shipment.can_receive &&
                String(shipment.status || '').toUpperCase() !==
                  'FULFILLMENT_CENTER_ASSIGNED' &&
                ['DISPATCHED', 'COMPLETED'].includes(
                  String(order.order_status || '').toUpperCase(),
                ) && (
                  <ConfirmShipmentReceiptButton
                    orderId={id}
                    shipment={shipment}
                    shipmentLabel={getShipmentDisplayLabel(index + 1)}
                    onSuccess={fetchOrder}
                  />
                )}
            </Box>
          ))}
        </CollapsibleSection>
      )}

      {/* Receipt Issues Reconciliation */}
      {hasVisibleReceiptIssueActions(order) && (
        <Card sx={{ mb: 3, p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Receipt Issues
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Missing Items Reconciliation */}
            {order.meta.receipt_issues.missing_items && order.meta.receipt_issues.missing_items.length > 0 && (() => {
              // Check if all missing items are already reconciled
              const missingItems = order.meta.receipt_issues.missing_items || [];
              const missingReconciledQuantities = order.meta.receipt_issues.missing_reconciled_quantities || {};
              const allMissingReconciled = missingItems.every(item => {
                const reconciledQty = missingReconciledQuantities[item.receipt_item_id] || 0;
                return reconciledQty >= item.missing_quantity;
              });
              
              // Don't show button if all missing items are reconciled
              if (allMissingReconciled) {
                return null;
              }
              
              return (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleOpenReconcileMissing}
                  sx={{ minWidth: 200 }}
                >
                  Reconcile Missing Items ({countUnreconciledMissingItems(order.meta.receipt_issues)})
                </Button>
              );
            })()}

            {/* Damaged Items - Return Shipment Received */}
            {order.meta.receipt_issues.damaged_items && order.meta.receipt_issues.damaged_items.length > 0 && (
              <>
                {order.meta.return_shipment_received !== true && (
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleMarkReturnShipmentReceived}
                    disabled={returnShipmentReceivedLoading}
                    startIcon={returnShipmentReceivedLoading ? <CircularProgress size={16} /> : null}
                    sx={{ minWidth: 200 }}
                  >
                    {returnShipmentReceivedLoading ? 'Processing...' : 'Return Shipment Received'}
                  </Button>
                )}
                {order.meta.return_shipment_received === true && (() => {
                  // Check if all damaged items are already reconciled
                  const damagedItems = order.meta.receipt_issues.damaged_items || [];
                  const damagedReconciledQuantities = order.meta.receipt_issues.damaged_reconciled_quantities || {};
                  const allDamagedReconciled = damagedItems.every(item => {
                    const reconciledQty = damagedReconciledQuantities[item.receipt_item_id] || 0;
                    return reconciledQty >= item.damaged_quantity;
                  });
                  
                  // Don't show button if all damaged items are reconciled
                  if (allDamagedReconciled) {
                    return null;
                  }
                  
                  return (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleOpenReconcileDamaged}
                      sx={{ minWidth: 200 }}
                    >
                      Reconcile Damaged Items ({countUnreconciledDamagedItems(order.meta.receipt_issues)})
                    </Button>
                  );
                })()}
              </>
            )}
          </Box>
        </Card>
      )}

      {/* Order Summary */}
      <Card sx={{ mb: 3, p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Order Summary
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
              Order ID
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>{order.order_id}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
              Shop Id
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {order.org_id != null ? order.org_id : '-'}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CheckBalanceButton orgId={order.org_id} compact />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
              Shop Name
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>{order.organization_name || '-'}</Typography>
          </Box>
          {/* TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT */}
          <Box>
            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
              Shipment SAP/Aspen integration
            </Typography>
            <Chip
              label={
                order.shipment_external_integration_enabled
                  ? 'Enabled'
                  : 'Disabled'
              }
              color={
                order.shipment_external_integration_enabled
                  ? 'success'
                  : 'error'
              }
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
              Status
            </Typography>
            <Chip
              label={getAdminDisplayStatusLabel(order)}
              color={getStatusColor(order.order_status, order)}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          </Box>
          {order.customer_id && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Customer ID
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>{order.customer_id}</Typography>
            </Box>
          )}
          {order.order_date && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Date Created
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>{formatDate(order.order_date, true)}</Typography>
            </Box>
          )}
          {(order.date_modified ?? order.dateModified) && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Date Modified
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>
                {formatDate(order.date_modified ?? order.dateModified, true)}
              </Typography>
            </Box>
          )}
          {initialOrderValue != null && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Initial Order Value
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {formatAmount(initialOrderValue)}
              </Typography>
            </Box>
          )}
          {order.amount !== undefined && order.amount !== null && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                {initialOrderValue != null ? 'Total Value' : 'Amount'}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#e90000', fontSize: '1.1rem' }}>
                {formatAmount(order.amount)}
              </Typography>
            </Box>
          )}
          {isPurchaseOrder(order) && (getOrderTotalWorthReceived(order) ?? 0) > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Total Worth Received
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#0F766E', fontSize: '1.1rem' }}>
                {formatAmount(getOrderTotalWorthReceived(order) ?? 0)}
              </Typography>
            </Box>
          )}
          {isPurchaseOrder(order) && (getOrderTotalWorthSent(order) ?? 0) > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Total Worth Sent
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#2563EB', fontSize: '1.1rem' }}>
                {formatAmount(getOrderTotalWorthSent(order) ?? 0)}
              </Typography>
            </Box>
          )}
          {isPurchaseOrder(order) && (getOrderTotalWorthRemaining(order) ?? 0) > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Total Worth Remaining
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#B45309', fontSize: '1.1rem' }}>
                {formatAmount(getOrderTotalWorthRemaining(order) ?? 0)}
              </Typography>
            </Box>
          )}
          {paymentStatusChip && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Payment Status
              </Typography>
              <Chip
                label={paymentStatusChip.label}
                color={paymentStatusChip.color}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}
          {isPurchaseOrder(order) && order.payment_proof_datetime && (
            <Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Payment proof date & time
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>
                {formatPaymentProofDateTime(order.payment_proof_datetime)}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      {hasStructuredPaymentProof && (
        <PaymentInformationSection
          summary={paymentSummary}
          requests={paymentProofRequestsSorted}
          hasLegacyFiles={legacyPaymentProofs.length > 0}
          feedback={paymentProofFeedback}
          onDismissFeedback={() => setPaymentProofFeedback(null)}
          canVerify={canVerifyPaymentProof}
          actionRequestId={paymentProofRequestActionId}
          rejectSubmitting={paymentProofRejectSubmitting}
          onVerify={handleVerifyPaymentProofRequest}
          canEditRejected={canEditRejectedPaymentProof}
          onEditRejected={requestEditRejectedPaymentProof}
          onReject={(req) => {
            setPaymentProofRejectTarget(req)
            setPaymentProofRejectReason('')
            setPaymentProofRejectError(null)
          }}
          canReverse={isSuperuserActor && order?.order_status === 'ACCEPTED'}
          reverseSubmitting={paymentProofReverseSubmitting}
          onReverse={(req) => {
            setPaymentProofReverseTarget(req)
            setPaymentProofReverseReason('')
            setPaymentProofReverseError(null)
          }}
          onExpandedChange={openPaymentSection}
          sectionStatus={paymentStatus}
          sectionError={paymentError}
          onRetrySection={() => ensurePaymentLoaded({ force: true })}
        />
      )}

      {order && isPurchaseOrder(order) && (
        <>
          <PaymentProofEntriesSection
            proofs={sortedStructuredProofs}
            legacyFiles={legacyPaymentProofs}
            bankAccountBySettingsId={bankAccountBySettingsId}
            canAddFile={canAdminUploadPaymentProof && sortedStructuredProofs.length < 5}
            onAddFile={requestAdminPaymentProofUpload}
            onPreview={openPaymentProofPreview}
            uploadFeedback={adminProofFeedback}
            onDismissUploadFeedback={() => setAdminProofFeedback(null)}
            onExpandedChange={openPaymentSection}
            sectionStatus={paymentStatus}
            sectionError={paymentError}
            onRetrySection={() => ensurePaymentLoaded({ force: true })}
          />
          <AdminPaymentProofUploadDialog
            open={adminProofUploadOpen}
            onClose={() => setAdminProofUploadOpen(false)}
            orderId={order.order_id}
            shwapnoAccounts={shwapnoReceivingAccounts}
            paymentSummary={paymentProofDetail?.summary ?? null}
            orderPayable={getOrderPayableAmount(order)}
            existingProofCount={
              sortedStructuredProofs.length ||
              (Array.isArray(paymentProofDetail?.payment_upload)
                ? paymentProofDetail.payment_upload.length
                : 0)
            }
            onUploaded={async () => {
              setAdminProofFeedback({
                severity: 'success',
                message: 'Payment proof uploaded successfully.',
              })
              await fetchOrder()
            }}
          />
        </>
      )}

      {/* Line Items */}
      {order.order_line_items && order.order_line_items.length > 0 && (
        <CollapsibleSection
          title="Line Items"
          headerExtra={
            <FormControlLabel
              control={
                <Switch
                  checked={detailedView}
                  onChange={handleDetailedViewChange}
                  color="primary"
                />
              }
              label="Detailed view"
              sx={{ mr: 0 }}
            />
          }
        >
            <TableContainer>
              <Table>
                {detailedView ? (
                  <>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product Name / Article Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Initial quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Modified quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      DK11 Stock
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      DK14 Stock
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      DK18 Stock
                    </TableCell>
                    {isPurchaseOrder(order) && (
                      <>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Shipped
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Remaining to ship
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Pending replacement
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Received
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Returned
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Remaining
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      MRP
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Selling Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Worth received
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Worth pending
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Discount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayLineItems.map((item) => {
                    const { received, remaining, damaged, isPurchase } = getLineReceiptColumns(item, order)
                    const worthReceived = isPurchase ? getLineWorthReceived(item, order) : null
                    const worthPending = isPurchase ? getLineWorthPending(item, order) : null
                    const articleCode = getArticleCode(item)
                    return (
                    <TableRow key={item._isAllocationGroup ? `group-${item.allocation_group_id ?? item.meta?.allocation_group_id}` : item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.product_name || '—'}
                        </Typography>
                        {articleCode && (
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                            Article Code: {articleCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{getLineInitialQuantity(item)}</TableCell>
                      <TableCell align="right">{getLineModifiedQuantity(item)}</TableCell>
                      {(() => {
                        const dk11 = formatDcStockCell(
                          getLineDcStock(dcStockByCode.DK11, item),
                          item.quantity,
                        )
                        const dk14 = formatDcStockCell(
                          getLineDcStock(dcStockByCode.DK14, item),
                          item.quantity,
                        )
                        const dk18 = formatDcStockCell(
                          getLineDcStock(dcStockByCode.DK18, item),
                          item.quantity,
                        )
                        return (
                          <>
                            <TableCell
                              align="right"
                              sx={{
                                color: dk11.low ? '#DC2626' : 'inherit',
                                fontWeight: dk11.low ? 600 : 'inherit',
                              }}
                            >
                              {dcStockLoading ? '…' : dk11.text}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: dk14.low ? '#DC2626' : 'inherit',
                                fontWeight: dk14.low ? 600 : 'inherit',
                              }}
                            >
                              {dcStockLoading ? '…' : dk14.text}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: dk18.low ? '#DC2626' : 'inherit',
                                fontWeight: dk18.low ? 600 : 'inherit',
                              }}
                            >
                              {dcStockLoading ? '…' : dk18.text}
                            </TableCell>
                          </>
                        )
                      })()}
                      {isPurchase && (
                        <>
                          <TableCell align="right">{item.shipped_quantity ?? 0}</TableCell>
                          <TableCell align="right">{item.remaining_to_ship ?? '—'}</TableCell>
                          <TableCell align="right">
                            {item.pending_replacement_ship_qty ??
                              item.meta?.pending_replacement_ship_qty ??
                              0}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right">
                        {isPurchase ? received : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: isPurchase && damaged > 0 ? '#DC2626' : 'inherit' }}>
                        {isPurchase ? damaged : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {isPurchase ? remaining : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const mrp = getLineMrp(item)
                          return mrp != null ? formatAmount(mrp) : '—'
                        })()}
                      </TableCell>
                      <TableCell align="right">{formatAmount(getLineSellingPriceTp(item))}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: isPurchase && worthReceived != null ? '#059669' : 'inherit',
                        }}
                      >
                        {isPurchase && worthReceived != null ? formatAmount(worthReceived) : '—'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: isPurchase && worthPending != null ? '#b45309' : 'inherit',
                        }}
                      >
                        {isPurchase && worthPending != null ? formatAmount(worthPending) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {item.discount_value > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                              {formatAmount(item.discount_value)}
                            </Typography>
                            {item.meta?.applied_promotion && (
                              <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600, bgcolor: '#fef3c7', px: 0.8, py: 0.3, borderRadius: 1, mt: 0.5 }}>
                                🎉 {item.meta.applied_promotion.discount_type === 'Amount' 
                                  ? `৳${parseFloat(item.meta.applied_promotion.discount_value).toFixed(2)} OFF` 
                                  : `${parseFloat(item.meta.applied_promotion.discount_value).toFixed(0)}% OFF`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatAmount(item.total_price)}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
                  </>
                ) : (
                  <>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Article Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Initial quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Modified quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      DK11 Stock
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      DK14 Stock
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      DK18 Stock
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      MRP
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Selling Price
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayLineItems.map((item) => {
                    const articleCode = getArticleCode(item)
                    const dk11 = formatDcStockCell(
                      getLineDcStock(dcStockByCode.DK11, item),
                      item.quantity,
                    )
                    const dk14 = formatDcStockCell(
                      getLineDcStock(dcStockByCode.DK14, item),
                      item.quantity,
                    )
                    const dk18 = formatDcStockCell(
                      getLineDcStock(dcStockByCode.DK18, item),
                      item.quantity,
                    )
                    return (
                      <TableRow key={item._isAllocationGroup ? `group-${item.allocation_group_id ?? item.meta?.allocation_group_id}` : item.id}>
                        <TableCell>{articleCode || '—'}</TableCell>
                        <TableCell>{item.product_name || '—'}</TableCell>
                        <TableCell align="right">{getLineInitialQuantity(item)}</TableCell>
                        <TableCell align="right">{getLineModifiedQuantity(item)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: dk11.low ? '#DC2626' : 'inherit',
                            fontWeight: dk11.low ? 600 : 'inherit',
                          }}
                        >
                          {dcStockLoading ? '…' : dk11.text}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: dk14.low ? '#DC2626' : 'inherit',
                            fontWeight: dk14.low ? 600 : 'inherit',
                          }}
                        >
                          {dcStockLoading ? '…' : dk14.text}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: dk18.low ? '#DC2626' : 'inherit',
                            fontWeight: dk18.low ? 600 : 'inherit',
                          }}
                        >
                          {dcStockLoading ? '…' : dk18.text}
                        </TableCell>
                        <TableCell align="right">
                        {(() => {
                          const mrp = getLineMrp(item)
                          return mrp != null ? formatAmount(mrp) : '—'
                        })()}
                      </TableCell>
                        <TableCell align="right">{formatAmount(getLineSellingPriceTp(item))}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
                  </>
                )}
              </Table>
            </TableContainer>
        </CollapsibleSection>
      )}

      {/* Return Information */}
      {order.return_request && order.return_request.return_items && order.return_request.return_items.length > 0 && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Return Information
            </Typography>
            {order.return_request.initiated_at && (
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Initiated: {formatDate(order.return_request.initiated_at)}
              </Typography>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Ordered Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Returned Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.return_request.return_items.map((returnItem) => {
                    const articleCode = getArticleCodeWithLineItems(
                      returnItem,
                      order.order_line_items,
                    )
                    return (
                    <TableRow key={returnItem.order_line_item_id}>
                      <TableCell>
                        {returnItem.product_name || '—'}
                        {articleCode && (
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                            Article Code: {articleCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{returnItem.ordered_quantity || 0}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#e90000' }}>
                        {returnItem.return_quantity}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {order.return_request.accepted_at && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  Return Accepted: {formatDate(order.return_request.accepted_at)}
                </Typography>
              </Box>
            )}
            {order.return_request.rejected_at && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#ffebee', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828' }}>
                  Return Rejected: {formatDate(order.return_request.rejected_at)}
                </Typography>
                {order.return_request.rejection_reason && (
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    Reason: {order.return_request.rejection_reason}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Card>
      )}

      {(() => {
        const priceAudit = Array.isArray(pricingTimelineData?.price_audit)
          ? pricingTimelineData.price_audit
          : []
        const lineItems = Array.isArray(pricingAuditData?.order_line_items)
          ? pricingAuditData.order_line_items
          : []
        const linePricingRows = lineItems
          .map((item) => ({
            item,
            pricing: getLinePricingSummary(item),
          }))
          .filter((row) => row.pricing)
        const pricingChannel =
          pricingAuditData?.pricing_channel ||
          pricingTimelineData?.pricing_channel ||
          order.meta?.pricing_channel
        return (
          <>
            <CollapsibleSection
              title="Pricing Audit"
              onExpandedChange={openPricingAuditSection}
            >
              <LazySectionMessage
                status={pricingAuditStatus}
                error={pricingAuditError}
                onRetry={() => ensurePricingAuditLoaded({ force: true })}
                isEmpty={!linePricingRows.length}
                empty="No pricing snapshots for this order."
              >
                {pricingChannel ? (
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Order created via {humanizePriceChannel(pricingChannel)}
                  </Typography>
                ) : null}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Line snapshots (at order time)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Article Code</TableCell>
                        <TableCell>Selling Price</TableCell>
                        <TableCell>MRP</TableCell>
                        <TableCell>Selling Price source</TableCell>
                        <TableCell>MRP source</TableCell>
                        <TableCell>Channel</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linePricingRows.map(({ item, pricing }) => (
                        <TableRow key={`price-snap-${item.id}`}>
                          <TableCell>{getArticleCode(item) || item.sku || '—'}</TableCell>
                          <TableCell>{formatAmount(pricing.selling_price_tp)}</TableCell>
                          <TableCell>{formatAmount(pricing.mrp)}</TableCell>
                          <TableCell>{humanizePriceSource(pricing.tp_source)}</TableCell>
                          <TableCell>{humanizePriceSource(pricing.mrp_source)}</TableCell>
                          <TableCell>{humanizePriceChannel(pricing.channel)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </LazySectionMessage>
            </CollapsibleSection>
            <CollapsibleSection
              title="Pricing Timeline"
              onExpandedChange={openPricingTimelineSection}
            >
              <LazySectionMessage
                status={pricingTimelineStatus}
                error={pricingTimelineError}
                onRetry={() => ensurePricingTimelineLoaded({ force: true })}
                isEmpty={!priceAudit.length}
                empty="No pricing events for this order."
              >
                {pricingChannel ? (
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Order created via {humanizePriceChannel(pricingChannel)}
                  </Typography>
                ) : null}
                {[...priceAudit].reverse().map((event, idx) => (
                  <Box
                    key={`price-audit-${idx}`}
                    sx={{
                      bgcolor: '#f9fafb',
                      p: 1.5,
                      borderRadius: 1,
                      mb: idx < priceAudit.length - 1 ? 1 : 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {describeAuditEvent(event)}
                    </Typography>
                    {event.at && (
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {formatDate(event.at)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </LazySectionMessage>
            </CollapsibleSection>
          </>
        )
      })()}

      {(() => {
        const modificationHistory = Array.isArray(
          modificationHistoryData?.modification_history,
        )
          ? modificationHistoryData.modification_history
          : []
        return (
          <CollapsibleSection
            title="Order Modification History"
            onExpandedChange={openModificationHistorySection}
          >
            <LazySectionMessage
              status={modificationHistoryStatus}
              error={modificationHistoryError}
              onRetry={() => ensureModificationHistoryLoaded({ force: true })}
              isEmpty={!modificationHistory.length}
              empty="No modifications recorded for this order."
            >
              {modificationHistory.map((historyEntry, historyIdx) => (
                <Box
                  key={`mod-history-${historyIdx}`}
                  sx={{
                    mb: historyIdx < modificationHistory.length - 1 ? 2 : 0,
                    pb: historyIdx < modificationHistory.length - 1 ? 2 : 0,
                    borderBottom:
                      historyIdx < modificationHistory.length - 1
                        ? '1px solid #eee'
                        : 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Modified
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {historyEntry.modified_at
                        ? formatDate(historyEntry.modified_at)
                        : ''}
                    </Typography>
                  </Box>
                  {historyEntry.modified_by_admin_name && (
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                      By: {historyEntry.modified_by_admin_name}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Changes
                  </Typography>
                  {(historyEntry.changes || []).map((change, changeIdx) => (
                    <Box
                      key={`mod-change-${changeIdx}`}
                      sx={{
                        bgcolor: '#f9fafb',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {change.product_name ||
                          `Product ${change.product_id || change.order_line_item_id}`}
                      </Typography>
                      {change.old_quantity !== undefined &&
                        change.new_quantity !== undefined && (
                          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                            Quantity:{' '}
                            <span style={{ textDecoration: 'line-through' }}>
                              {change.old_quantity}
                            </span>{' '}
                            → {change.new_quantity}
                          </Typography>
                        )}
                      {change.old_unit_price !== undefined &&
                        change.new_unit_price !== undefined && (
                          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                            Selling Price:{' '}
                            <span style={{ textDecoration: 'line-through' }}>
                              {formatAmount(change.old_unit_price)}
                            </span>{' '}
                            → {formatAmount(change.new_unit_price)}
                          </Typography>
                        )}
                      {change.sku && (
                        <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                          Article Code: {change.sku}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </LazySectionMessage>
          </CollapsibleSection>
        )
      })()}

      {/* Order Timeline */}
      {(() => {
        const transitions = Array.isArray(timelineData?.order_state_transitions)
          ? timelineData.order_state_transitions
          : []
        const sortedTransitions = [...transitions].sort((a, b) => {
          const dateA = new Date(a.created_at).getTime()
          const dateB = new Date(b.created_at).getTime()
          return dateB - dateA // Descending order (most recent first)
        })
        
        return (
          <CollapsibleSection
            title="Order Timeline"
            mb={0}
            onExpandedChange={openTimelineSection}
          >
            <LazySectionMessage
              status={timelineStatus}
              error={timelineError}
              onRetry={() => ensureTimelineLoaded({ force: true })}
              isEmpty={!sortedTransitions.length}
              empty="No timeline entries for this order."
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sortedTransitions.map((transition, index) => (
                  <Box key={transition.id}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          {transition.from_state ? (
                            <>
                              <Chip
                                label={getAdminStatusLabel(transition.from_state, order)}
                                size="small"
                                variant="outlined"
                                sx={{ bgcolor: '#f5f5f5' }}
                              />
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                →
                              </Typography>
                            </>
                          ) : null}
                          <Chip
                            label={getTimelineToStateLabel(transition, order)}
                            color={
                              getTimelineToStateLabel(transition, order) ===
                              'Payment Proof Edited'
                                ? 'info'
                                : transition.to_state ===
                                      'CLOSED_WITH_PARTIAL_DELIVERY' ||
                                    transition.meta?.closed_with_partial_delivery
                                  ? 'warning'
                                  : getStatusColor(transition.to_state)
                            }
                            size="small"
                            variant="outlined"
                          />
                        {transition.actor_type && (
                          <Chip
                            label={transition.actor_type}
                            size="small"
                            variant="outlined"
                            sx={{ bgcolor: '#e3f2fd' }}
                          />
                        )}
                      </Box>
                      {(() => {
                        const toState = String(transition.to_state || '').toUpperCase()
                        const isCompletedTransition =
                          toState === 'COMPLETED' ||
                          toState === 'CLOSED_WITH_PARTIAL_DELIVERY' ||
                          Boolean(transition.meta?.closed_with_partial_delivery)
                        const completionReasonFromMeta =
                          isCompletedTransition &&
                          transition.meta?.completion_reason != null &&
                          String(transition.meta.completion_reason).trim()
                            ? String(transition.meta.completion_reason).trim()
                            : null
                        const completedReasonMatch =
                          isCompletedTransition &&
                          !completionReasonFromMeta &&
                          typeof transition.reason === 'string'
                            ? transition.reason.match(
                                /^Order (?:completed|closed with partial delivery) and locked by admin:\s*(.+)$/s,
                              )
                            : null
                        const completionReason =
                          completionReasonFromMeta ||
                          (completedReasonMatch
                            ? completedReasonMatch[1].trim()
                            : null)
                        const displayReason =
                          completionReason &&
                          typeof transition.reason === 'string' &&
                          (transition.reason.startsWith(
                            'Order completed and locked by admin',
                          ) ||
                            transition.reason.startsWith(
                              'Order closed with partial delivery and locked by admin',
                            ))
                            ? transition.reason.replace(/:\s*.+$/s, '').trim()
                            : transition.reason

                        return (
                          <>
                            {displayReason ? (
                              <Typography
                                variant="body2"
                                sx={{ color: '#666', mb: 0.5 }}
                              >
                                {displayReason}
                              </Typography>
                            ) : null}
                            {completionReason ? (
                              <Typography
                                variant="body2"
                                sx={{ color: '#666', mb: 0.5, display: 'block' }}
                              >
                                Reason: {completionReason}
                              </Typography>
                            ) : null}
                          </>
                        )
                      })()}
                      {(() => {
                        const reconciliationEventTypes = new Set([
                          'RETURN_ITEMS_REPORTED',
                          'RETURN_SHIPMENT_RECEIVED',
                          'MISSING_ITEMS_RECONCILED',
                          'DAMAGED_ITEMS_RECONCILED',
                          'REPLACEMENT_SHIPMENT_DISPATCHED',
                        ])
                        const paymentProofDetails =
                          PAYMENT_PROOF_TIMELINE_STATES.has(transition.to_state)
                            ? getPaymentProofTimelineDetails(
                                transition,
                                order?.payment_proof_datetime,
                              )
                            : []
                        const reconciliationDetails =
                          reconciliationEventTypes.has(transition.to_state)
                            ? getReconciliationTimelineDetails(transition)
                            : []
                        const closedPartialDetails =
                          transition.to_state === 'CLOSED_WITH_PARTIAL_DELIVERY' ||
                          transition.meta?.closed_with_partial_delivery
                            ? getClosedWithPartialDeliveryTimelineDetails(transition)
                            : []
                        const isShipmentCreateFailed =
                          String(transition.to_state || '').toUpperCase() ===
                          'SHIPMENT_CREATE_FAILED'
                        const shipmentCreateFailedDetails = isShipmentCreateFailed
                          ? getShipmentCreateFailedTimelineDetails(transition)
                          : []
                        const detailLines = [
                          ...paymentProofDetails,
                          ...reconciliationDetails,
                          ...closedPartialDetails,
                          ...shipmentCreateFailedDetails,
                        ]
                        if (detailLines.length === 0 && !isShipmentCreateFailed) {
                          return null
                        }
                        return (
                          <>
                            {detailLines.length > 0 ? (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1.5,
                                  bgcolor: '#f9fafb',
                                  borderRadius: 1,
                                }}
                              >
                                {detailLines.map((line, i) => (
                                  <Typography
                                    key={`timeline-detail-${transition.id}-${i}`}
                                    variant="body2"
                                    sx={{
                                      color: '#444',
                                      mb: i < detailLines.length - 1 ? 0.5 : 0,
                                    }}
                                  >
                                    {line}
                                  </Typography>
                                ))}
                              </Box>
                            ) : null}
                            {isShipmentCreateFailed ? (
                              <ShipmentCreateFailureStatus
                                hasRequest={
                                  transition.meta?.has_sap_sales_order_request ===
                                  true
                                }
                                hasResponse={
                                  transition.meta?.has_api_response === true
                                }
                                requestViewLoading={
                                  integrationViewKey ===
                                  `create-fail-request:${transition.id}`
                                }
                                responseViewLoading={
                                  integrationViewKey ===
                                  `create-fail-response:${transition.id}`
                                }
                                onRequestViewPayload={() =>
                                  openShipmentCreateFailureView(
                                    'request',
                                    transition,
                                  )
                                }
                                onRequestViewResponse={() =>
                                  openShipmentCreateFailureView(
                                    'response',
                                    transition,
                                  )
                                }
                              />
                            ) : null}
                          </>
                        )
                      })()}
                      {Array.isArray(transition.meta?.modification_summary) &&
                        transition.meta.modification_summary.length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              bgcolor: '#f9fafb',
                              borderRadius: 1,
                            }}
                          >
                            {transition.meta.modification_summary.map((line, i) => (
                              <Typography
                                key={`mod-summary-${transition.id}-${i}`}
                                variant="body2"
                                sx={{
                                  color: '#444',
                                  whiteSpace: 'pre-line',
                                  mb:
                                    i < transition.meta.modification_summary.length - 1
                                      ? 1
                                      : 0,
                                }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {(transition.actor_name || transition.actor_email) && (
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            by {transition.actor_name || transition.actor_email}
                            {transition.actor_name && transition.actor_email && (
                              <span style={{ color: '#999', marginLeft: '4px' }}>
                                ({transition.actor_email})
                              </span>
                            )}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {formatDate(transition.created_at)}
                        </Typography>
                      </Box>
                      </Box>
                    </Box>
                    {index < sortedTransitions.length - 1 && (
                      <Divider sx={{ mt: 2 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </LazySectionMessage>
          </CollapsibleSection>
        )
      })()}


      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Proof
          </Typography>
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{ color: '#666' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt={previewFileName || 'Payment proof'}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button
            onClick={() => setImagePreviewOpen(false)}
            color="inherit"
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (previewImageUrl) {
                const link = document.createElement('a')
                link.href = previewImageUrl
                link.download = previewFileName || 'payment-proof.jpg'
                link.target = '_blank'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }
            }}
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#45a049' },
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shipment reference field save success */}
      <Snackbar
        open={Boolean(shipmentReferenceSaveMessage)}
        autoHideDuration={3000}
        onClose={() => setShipmentReferenceSaveMessage(null)}
        message={shipmentReferenceSaveMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
      <Snackbar
        open={Boolean(recordingMessage)}
        autoHideDuration={4000}
        onClose={() => setRecordingMessage(null)}
        message={recordingMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
      <Dialog
        open={Boolean(recordingResponseView)}
        onClose={() => setRecordingResponseView(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {recordingResponseView?.heading ||
              `${recordingResponseView?.title || 'Shipment'} recording response`}
          </Typography>
          <IconButton
            aria-label="Close recording response"
            onClick={() => setRecordingResponseView(null)}
            sx={{ color: '#666' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              fontSize: 13,
              bgcolor: '#f6f8fa',
              borderRadius: 1,
            }}
          >
            {formatSalesOrderRecordingResponse(recordingResponseView?.response)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordingResponseView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(podToastMessage)}
        autoHideDuration={3000}
        onClose={() => setPodToastMessage(null)}
        message={podToastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      {/* Reconcile Missing Items Modal */}
      <Dialog
        open={reconcileMissingModalOpen}
        onClose={() => setReconcileMissingModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reconcile Missing Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Select the quantity of missing items to reconcile. Reconciled items will be added back to admin inventory.
          </Typography>
          {order?.meta?.receipt_issues?.missing_items?.length > 0 ? (
            order.meta.receipt_issues.missing_items.map((item) => {
              // Try to find product name from order line items
              const lineItem = order?.order_line_items?.find(li => {
                const adminProductId = li?.meta?.admin_product_id || li?.admin_product_id
                return adminProductId === item.product_id
              })
              const productName = lineItem?.product_name || '—'
              const articleCode = getArticleCode(lineItem)
              const alreadyReconciled = order?.meta?.receipt_issues?.missing_reconciled_quantities?.[item.receipt_item_id] || 0
              const remainingToReconcile = item.missing_quantity - alreadyReconciled
              
              return (
                <Box key={item.receipt_item_id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {productName}
                  </Typography>
                  {articleCode && (
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                      Article Code: {articleCode}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Missing Quantity: {item.missing_quantity}
                    {alreadyReconciled > 0 && (
                      <span style={{ color: '#4caf50', marginLeft: 8 }}>
                        (Already reconciled: {alreadyReconciled})
                      </span>
                    )}
                  </Typography>
                  {remainingToReconcile > 0 ? (
                    <TextField
                      label="Reconcile Quantity"
                      type="number"
                      value={reconcileMissingData[item.receipt_item_id] || 0}
                      onChange={(e) => {
                        const value = Math.max(0, Math.min(remainingToReconcile, Number(e.target.value) || 0))
                        setReconcileMissingData(prev => ({
                          ...prev,
                          [item.receipt_item_id]: value
                        }))
                      }}
                      inputProps={{ min: 0, max: remainingToReconcile }}
                      fullWidth
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: '#4caf50', fontStyle: 'italic' }}>
                      Fully reconciled
                    </Typography>
                  )}
                </Box>
              )
            })
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
              No missing items to reconcile
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileMissingModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReconcileMissing}
            variant="contained"
            color="warning"
            disabled={reconcileMissingLoading}
            startIcon={reconcileMissingLoading ? <CircularProgress size={16} /> : null}
          >
            {reconcileMissingLoading ? 'Reconciling...' : 'Reconcile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile Damaged Items Modal */}
      <Dialog
        open={reconcileDamagedModalOpen}
        onClose={() => setReconcileDamagedModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reconcile Damaged Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Select the quantity of damaged items to reconcile. Reconciled items will be added back to admin inventory.
          </Typography>
          {order?.meta?.receipt_issues?.damaged_items?.length > 0 ? (
            order.meta.receipt_issues.damaged_items.map((item) => {
              // Try to find product name from order line items
              const lineItem = order?.order_line_items?.find(li => {
                const adminProductId = li?.meta?.admin_product_id || li?.admin_product_id
                return adminProductId === item.product_id
              })
              const productName = lineItem?.product_name || '—'
              const articleCode = getArticleCode(lineItem)
              const alreadyReconciled = order?.meta?.receipt_issues?.damaged_reconciled_quantities?.[item.receipt_item_id] || 0
              const remainingToReconcile = item.damaged_quantity - alreadyReconciled
              
              return (
                <Box key={item.receipt_item_id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {productName}
                  </Typography>
                  {articleCode && (
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                      Article Code: {articleCode}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Damaged Quantity: {item.damaged_quantity}
                    {item.shipment_id != null && (
                      <span style={{ marginLeft: 8 }}>
                        (Shipment ID: {item.shipment_id})
                      </span>
                    )}
                    {alreadyReconciled > 0 && (
                      <span style={{ color: '#4caf50', marginLeft: 8 }}>
                        (Already reconciled: {alreadyReconciled})
                      </span>
                    )}
                  </Typography>
                  {remainingToReconcile > 0 ? (
                    <TextField
                      label="Reconcile Quantity"
                      type="number"
                      value={reconcileDamagedData[item.receipt_item_id] || 0}
                      onChange={(e) => {
                        const value = Math.max(0, Math.min(remainingToReconcile, Number(e.target.value) || 0))
                        setReconcileDamagedData(prev => ({
                          ...prev,
                          [item.receipt_item_id]: value
                        }))
                      }}
                      inputProps={{ min: 0, max: remainingToReconcile }}
                      fullWidth
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: '#4caf50', fontStyle: 'italic' }}>
                      Fully reconciled
                    </Typography>
                  )}
                </Box>
              )
            })
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
              No damaged items to reconcile
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDamagedModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReconcileDamaged}
            variant="contained"
            color="error"
            disabled={reconcileDamagedLoading}
            startIcon={reconcileDamagedLoading ? <CircularProgress size={16} /> : null}
          >
            {reconcileDamagedLoading ? 'Reconciling...' : 'Reconcile'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!paymentProofReverseTarget}
        onClose={closePaymentProofReverseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Reverse payment proof request
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {paymentProofReverseError && (
            <Alert severity="error" onClose={() => setPaymentProofReverseError(null)} sx={{ mb: 2 }}>
              {paymentProofReverseError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Move request <strong>#{paymentProofReverseTarget?.id ?? '—'}</strong> from{' '}
            <strong>{String(paymentProofReverseTarget?.status || '').toLowerCase() || '—'}</strong>{' '}
            back to pending? It will need to be verified or rejected again.
          </Typography>
          <TextField
            autoFocus
            label="Reversal reason"
            value={paymentProofReverseReason}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= PAYMENT_PROOF_REJECT_REASON_MAX) {
                setPaymentProofReverseReason(value)
                setPaymentProofReverseError(null)
              }
            }}
            fullWidth
            multiline
            rows={4}
            required
            disabled={paymentProofReverseSubmitting}
            placeholder="Enter reason for reversal…"
            helperText={`${paymentProofReverseReason.length}/${PAYMENT_PROOF_REJECT_REASON_MAX} characters`}
            inputProps={{ maxLength: PAYMENT_PROOF_REJECT_REASON_MAX }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={closePaymentProofReverseDialog} disabled={paymentProofReverseSubmitting} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReversePaymentProofRequest}
            variant="contained"
            color="warning"
            disabled={paymentProofReverseSubmitting || !paymentProofReverseReason.trim()}
            startIcon={
              paymentProofReverseSubmitting ? <CircularProgress size={16} /> : undefined
            }
          >
            {paymentProofReverseSubmitting ? 'Reversing…' : 'Reverse to pending'}
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentProofVerifyDialog
        open={Boolean(paymentProofVerifyTarget)}
        request={paymentProofVerifyTarget}
        onClose={() => {
          if (paymentProofRequestActionId) return
          setPaymentProofVerifyTarget(null)
        }}
        onConfirm={handleConfirmVerifyPaymentProofRequest}
        submitting={
          paymentProofVerifyTarget != null &&
          paymentProofRequestActionId === paymentProofVerifyTarget.id
        }
      />

      <PaymentProofBalanceChoiceDialog
        open={balanceChoiceVisible}
        applying={balanceChoiceApplying}
        error={balanceChoiceError}
        onClose={closeBalanceChoice}
        onProceedWithBalance={proceedWithAvailableBalance}
        onUploadProofDeposit={confirmUploadProofDeposit}
      />

      <PaymentProofEditAfterRejectDialog
        open={Boolean(paymentProofEditTarget)}
        orderId={order?.order_id}
        requestId={paymentProofEditTarget?.id}
        onClose={() => setPaymentProofEditTarget(null)}
        onSaved={async () => {
          setPaymentProofEditTarget(null)
          setPaymentProofFeedback({
            severity: 'success',
            message: 'Payment proof updated and sent for re-verification.',
          })
          await fetchOrder()
        }}
      />

      <Dialog
        open={!!paymentProofRejectTarget}
        onClose={closePaymentProofRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Reject payment proof request
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {paymentProofRejectError && (
            <Alert severity="error" onClose={() => setPaymentProofRejectError(null)} sx={{ mb: 2 }}>
              {paymentProofRejectError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reject request{' '}
            <strong>#{paymentProofRejectTarget?.id ?? '—'}</strong>? The shop or an admin can edit
            and resubmit the payment proof from the rejected queue or this order.
          </Typography>
          <TextField
            autoFocus
            label="Rejection reason"
            value={paymentProofRejectReason}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= PAYMENT_PROOF_REJECT_REASON_MAX) {
                setPaymentProofRejectReason(value)
                setPaymentProofRejectError(null)
              }
            }}
            fullWidth
            multiline
            rows={4}
            required
            disabled={paymentProofRejectSubmitting}
            placeholder="Enter reason for rejection…"
            error={
              !!paymentProofRejectReason &&
              !paymentProofRejectReason.trim()
            }
            helperText={`${paymentProofRejectReason.length}/${PAYMENT_PROOF_REJECT_REASON_MAX} characters`}
            inputProps={{ maxLength: PAYMENT_PROOF_REJECT_REASON_MAX }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={closePaymentProofRejectDialog} disabled={paymentProofRejectSubmitting} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRejectPaymentProofRequest}
            variant="contained"
            color="error"
            disabled={paymentProofRejectSubmitting || !paymentProofRejectReason.trim()}
            startIcon={
              paymentProofRejectSubmitting ? <CircularProgress size={16} /> : undefined
            }
            sx={{
              bgcolor: '#d32f2f',
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {paymentProofRejectSubmitting ? 'Rejecting…' : 'Reject request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OrderDetailPage
